<template>
  <div class="pos">
    <main class="pos__left">
    <!-- TOP BAR: search (biller's primary tool) + refresh -->
    <div class="pos__topbar">
      <div class="pos__search-wrap">
        <svg class="pos__search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          ref="searchInput"
          v-model="productSearch"
          class="pos__search"
          type="text"
          placeholder="Scan barcode or search by name, SKU, shortcode…"
          autocomplete="off"
          @keydown.enter.prevent="onSearchEnter"
        />
        <span v-if="productSearch" class="pos__search-clear" @click="productSearch = ''">×</span>
      </div>
      <button type="button" class="pos__refresh" :disabled="isLoading" @click="loadBootstrap">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :class="{ 'is-spinning': isLoading }">
          <polyline points="23 4 23 10 17 10"/>
          <polyline points="1 20 1 14 7 14"/>
          <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
        </svg>
        <span>{{ isLoading ? 'Loading' : 'Refresh' }}</span>
      </button>
      <button
        type="button"
        class="pos__ledger"
        title="POS Sales report"
        aria-label="POS Sales report"
        @click="$router.push('/pos-sales')"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
          <line x1="8" y1="7" x2="16" y2="7"/>
          <line x1="8" y1="11" x2="14" y2="11"/>
        </svg>
      </button>
    </div>

    <CAlert v-if="errorMessage" color="danger" show class="pos__alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="pos__alert pos__alert--ok">{{ successMessage }}</CAlert>

    <!-- ORG TABS — reference-style row of tabs with icons -->
    <nav class="pos__tabs">
      <button
        type="button"
        class="pos__tab"
        :class="{ 'is-active': productOrgFilter === 'all' }"
        @click="productOrgFilter = 'all'"
      >
        <span class="pos__tab-icon pos__tab-icon--grid" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <rect x="3" y="3" width="6.5" height="6.5" rx="1.4"/>
            <rect x="14.5" y="3" width="6.5" height="6.5" rx="1.4"/>
            <rect x="3" y="14.5" width="6.5" height="6.5" rx="1.4"/>
            <rect x="14.5" y="14.5" width="6.5" height="6.5" rx="1.4"/>
          </svg>
        </span>
        <span class="pos__tab-label">All Products</span>
      </button>
      <button
        v-for="org in organisations"
        :key="org.id"
        type="button"
        class="pos__tab"
        :class="[orgPillClass(org.id), { 'is-active': String(productOrgFilter) === String(org.id) }]"
        @click="productOrgFilter = org.id"
      >
        <span class="pos__tab-icon">
          <img v-if="orgLogo(org.name)" :src="orgLogo(org.name)" :alt="org.name" />
          <span v-else class="pos__tab-icon-fallback">{{ orgShort(org.id).slice(0,1) }}</span>
        </span>
        <span class="pos__tab-label">{{ org.name }}</span>
      </button>
    </nav>

      <section class="pos__catalogue">
        <div v-if="!filteredItems.length" class="pos__empty">
          <div class="pos__empty-icon">📦</div>
          <div>{{ items.length ? 'No products match this search.' : 'Loading products…' }}</div>
        </div>
        <div v-else class="pos__grid">
          <article
            v-for="item in filteredItems"
            :key="item.id"
            class="pos__tile"
            :class="orgPillClass(item.organisation_id)"
          >
            <div class="pos__tile-media" @click="addItemToCart(item)">
              <template v-if="item.image">
                <img class="pos__tile-photo" :src="item.image" :alt="item.name" loading="lazy" />
              </template>
              <template v-else>
                <img v-if="orgLogo(orgName(item.organisation_id))" class="pos__tile-watermark" :src="orgLogo(orgName(item.organisation_id))" :alt="orgName(item.organisation_id)" />
                <span class="pos__tile-shortcode">{{ item.shortcode || item.sku || item.name.slice(0,3).toUpperCase() }}</span>
              </template>
              <span class="pos__tile-orgbadge">{{ orgShort(item.organisation_id) }}</span>
            </div>
            <div class="pos__tile-body">
              <h3 class="pos__tile-name">{{ item.name }}</h3>
              <p class="pos__tile-desc">{{ tileSubtitle(item) }}</p>
              <div class="pos__tile-priceline">
                <span class="pos__tile-price">{{ formatCurrency(item.price) }}</span>
                <span class="pos__tile-sku">{{ item.sku || '—' }}</span>
              </div>
              <button type="button" class="pos__tile-add" @click="addItemToCart(item)">
                <span aria-hidden="true">+</span> Add to cart
              </button>
            </div>
          </article>
        </div>
      </section>
    </main>

    <aside class="pos__cart">
      <!-- Customer search row at the top of the cart panel -->
      <div class="pos__customer">
        <div class="pos__customer-row">
          <AppDropdown
            class="pos__customer-country"
            :value="countryCode"
            :options="countryOptions"
            searchable
            search-placeholder="Search country…"
            @update:value="countryCode = $event"
          />
          <div class="pos__customer-phone-wrap">
            <svg class="pos__customer-phone-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
            </svg>
            <input
              class="pos__customer-phone"
              type="tel"
              maxlength="15"
              placeholder="Customer phone (10 digits)"
              :value="contact.phone"
              @input="setPhone($event.target.value)"
              @keydown.enter.prevent="searchCustomer"
            />
          </div>
          <button
            type="button"
            class="pos__customer-find"
            :disabled="isSearching || contact.phone.length !== 10"
            @click="searchCustomer"
          >{{ isSearching ? '…' : 'Find' }}</button>
        </div>

        <small v-if="searchHint" :class="['pos__hint', searchHintClass]">{{ searchHint }}</small>

        <div v-if="contact.id || contact.name || contactCalls.length || contactOrders.length" class="pos__customer-card">
          <div class="pos__customer-name">{{ contact.name || '—' }}</div>
          <div v-if="lastCall" class="pos__lastcall">
            <span class="pos__lastcall-dot" aria-hidden="true"></span>
            Last contact: <strong>{{ lastCall.caller || 'Unknown' }}</strong>
            <span class="pos__lastcall-meta">· {{ lastCall.date }}</span>
          </div>
          <div v-if="contactCalls.length || contactOrders.length" class="pos__customer-actions">
            <button type="button" class="pos__chip-sm pos__chip-sm--accent" @click="historyOpen = true">
              History <span class="pos__chip-sm-badge">{{ contactCalls.length + contactOrders.length }}</span>
            </button>
          </div>
        </div>
      </div>

      <header class="pos__cart-head">
        <h2>Cart</h2>
        <span v-if="cart.length" class="pos__cart-count">{{ totalItemCount }} item{{ totalItemCount === 1 ? '' : 's' }}</span>
      </header>

      <div class="pos__lines">
        <div v-if="!cart.length" class="pos__lines-empty">
          <div class="pos__lines-empty-big">Cart is empty</div>
          <div class="pos__lines-empty-sub">Scan or tap products to start a bill</div>
        </div>

        <div v-for="group in cartByOrg" :key="`grp-${group.organisation_id}`" class="pos__group">
          <div v-if="cartByOrg.length > 1" :class="['pos__group-head', orgPillClass(group.organisation_id)]">
            <span>{{ orgName(group.organisation_id) }}</span>
            <span>{{ formatCurrency(group.subtotal) }}</span>
          </div>
          <ul class="pos__line-list">
            <li v-for="line in group.lines" :key="line.cart_key" class="pos__line">
              <div :class="['pos__line-thumb', orgPillClass(line.organisation_id), { 'has-photo': line.image }]">
                <img v-if="line.image" :src="line.image" :alt="line.name" loading="lazy" />
                <img v-else-if="orgLogo(orgName(line.organisation_id))" :src="orgLogo(orgName(line.organisation_id))" :alt="orgName(line.organisation_id)" />
              </div>
              <div class="pos__line-info">
                <div class="pos__line-name">{{ line.name }}</div>
                <div class="pos__line-meta">{{ line.sku || line.shortcode || '—' }}</div>
                <div class="pos__line-price">{{ formatCurrency(line.total) }}</div>
              </div>
              <div class="pos__line-qty">
                <button type="button" class="pos__qty-btn pos__qty-btn--minus" :disabled="line.quantity <= 1" @click="decQty(line)" aria-label="Decrease">−</button>
                <input
                  class="pos__qty-input"
                  type="number"
                  min="1"
                  :value="line.quantity"
                  @input="setQty(line, $event.target.value)"
                />
                <button type="button" class="pos__qty-btn pos__qty-btn--plus" @click="incQty(line)" aria-label="Increase">+</button>
              </div>
              <button type="button" class="pos__line-x" aria-label="Remove" @click="removeLine(line)">×</button>
            </li>
          </ul>
        </div>
      </div>

      <!-- Cart footer: running total + proceed CTA -->
      <footer class="pos__cart-foot">
        <div class="pos__running">
          <div class="pos__running-row">
            <span>{{ totalItemCount }} item{{ totalItemCount === 1 ? '' : 's' }}</span>
            <span>{{ formatCurrency(cartSubtotal) }}</span>
          </div>
          <div class="pos__running-row pos__running-row--grand">
            <span>Total</span>
            <span>{{ formatCurrency(cartGrandTotal) }}</span>
          </div>
        </div>
        <button
          type="button"
          class="pos__proceed"
          :disabled="!cart.length || isSubmitting"
          @click="openCheckout()"
        >
          <span>Proceed to Checkout</span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
            <line x1="5" y1="12" x2="19" y2="12"/>
            <polyline points="12 5 19 12 12 19"/>
          </svg>
        </button>
        <button
          v-if="cart.length"
          type="button"
          class="pos__reset-link"
          :disabled="isSubmitting"
          @click="resetAll(false)"
        >Reset cart</button>
      </footer>
    </aside>

    <!-- Checkout modal — single place to confirm customer, payment, totals -->
    <transition name="pos-modal">
      <div v-if="checkoutOpen" class="pos-modal pos-checkout" @click.self="closeCheckout()">
        <div class="pos-modal__panel pos-checkout__panel">
          <header class="pos-checkout__head">
            <div>
              <h2>Checkout</h2>
              <p>{{ totalItemCount }} item{{ totalItemCount === 1 ? '' : 's' }} · {{ formatCurrency(cartGrandTotal) }}<span v-if="bills.length > 1"> · {{ bills.length }} bills</span></p>
            </div>
            <button type="button" class="pos-modal__close" @click="closeCheckout()" aria-label="Close">×</button>
          </header>

          <div class="pos-checkout__body">
            <!-- CUSTOMER -->
            <section class="pos-checkout__section" ref="checkoutCustomerSection">
              <header class="pos-checkout__sec-head">
                <h3>Customer</h3>
              </header>

              <div>
                <div class="pos-checkout__phone-row">
                  <AppDropdown
                    class="pos__customer-country"
                    :value="countryCode"
                    :options="countryOptions"
                    searchable
                    search-placeholder="Search country…"
                    @update:value="countryCode = $event"
                  />
                  <div class="pos__customer-phone-wrap">
                    <svg class="pos__customer-phone-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                    </svg>
                    <input
                      ref="checkoutPhoneInput"
                      class="pos__customer-phone"
                      type="tel"
                      maxlength="15"
                      placeholder="Customer phone (10 digits)"
                      :value="contact.phone"
                      @input="setPhone($event.target.value)"
                      @keydown.enter.prevent="searchCustomer"
                    />
                  </div>
                  <button
                    type="button"
                    class="pos__customer-find"
                    :disabled="isSearching || contact.phone.length !== 10"
                    @click="searchCustomer"
                  >{{ isSearching ? '…' : 'Find' }}</button>
                </div>

                <small v-if="searchHint" :class="['pos__hint', searchHintClass]">{{ searchHint }}</small>

                <div v-if="lastCall" class="pos__lastcall pos-checkout__lastcall">
                  <span class="pos__lastcall-dot" aria-hidden="true"></span>
                  Last contact: <strong>{{ lastCall.caller || 'Unknown' }}</strong>
                  <span class="pos__lastcall-meta">· {{ lastCall.date }}</span>
                  <button
                    v-if="contactCalls.length || contactOrders.length"
                    type="button"
                    class="pos__chip-sm pos__chip-sm--accent pos-checkout__history-chip"
                    @click="historyOpen = true"
                  >
                    History <span class="pos__chip-sm-badge">{{ contactCalls.length + contactOrders.length }}</span>
                  </button>
                </div>

                <div class="pos-checkout__fields">
                  <input
                    class="pos__input pos__input--sm"
                    placeholder="Customer name"
                    :value="contact.name"
                    @input="contact.name = String($event.target.value || '').replace(/[^a-zA-Z\s.]/g, '')"
                  />
                  <div class="pos-required-field">
                    <textarea
                      class="pos__input pos__input--sm pos__textarea"
                      rows="2"
                      placeholder="Address"
                      :value="contact.address"
                      @input="contact.address = String($event.target.value || '')"
                    ></textarea>
                    <span class="pos-required-mark" aria-hidden="true">*</span>
                  </div>
                  <div class="pos__triple">
                    <input
                      class="pos__input pos__input--sm"
                      placeholder="Pincode"
                      type="tel"
                      maxlength="6"
                      :value="contact.pincode"
                      @input="contact.pincode = String($event.target.value || '').replace(/\D/g,'').slice(0,6)"
                    />
                    <input
                      class="pos__input pos__input--sm"
                      placeholder="District"
                      :value="contact.district"
                      @input="contact.district = String($event.target.value || '')"
                    />
                    <input
                      class="pos__input pos__input--sm"
                      placeholder="State"
                      :value="contact.state"
                      @input="contact.state = String($event.target.value || '')"
                    />
                  </div>
                </div>
              </div>
            </section>

            <!-- ORDER ITEMS RECAP -->
            <section class="pos-checkout__section">
              <header class="pos-checkout__sec-head">
                <h3>Order items</h3>
                <span class="pos-checkout__sec-meta">{{ cart.length }} line{{ cart.length === 1 ? '' : 's' }}</span>
              </header>
              <ul class="pos-checkout__items">
                <li v-for="line in cart" :key="`co-${line.cart_key}`" class="pos-checkout__item">
                  <div :class="['pos__line-thumb', 'pos-checkout__item-thumb', orgPillClass(line.organisation_id), { 'has-photo': line.image }]">
                    <img v-if="line.image" :src="line.image" :alt="line.name" loading="lazy" />
                    <img v-else-if="orgLogo(orgName(line.organisation_id))" :src="orgLogo(orgName(line.organisation_id))" :alt="orgName(line.organisation_id)" />
                  </div>
                  <div class="pos-checkout__item-info">
                    <div class="pos-checkout__item-name">{{ line.name }}</div>
                    <div class="pos-checkout__item-meta">{{ formatCurrency(line.price) }} × {{ line.quantity }}</div>
                  </div>
                  <div class="pos-checkout__item-total">{{ formatCurrency(line.total) }}</div>
                </li>
              </ul>
            </section>

            <!-- PAYMENT -->
            <section class="pos-checkout__section">
              <header class="pos-checkout__sec-head">
                <h3>Payment method</h3>
              </header>
              <div class="pos-checkout__pay-tiles">
                <button
                  v-for="option in paymentMethodOptions"
                  :key="`pay-${option.value}`"
                  type="button"
                  :class="['pos-checkout__pay-btn', { 'is-active': paymentMethod === option.value }]"
                  @click="paymentMethod = option.value"
                >
                  <img
                    v-if="option.value === 'federal_bank_team'"
                    src="/icons/fedbank.png"
                    alt=""
                    class="pos-checkout__pay-icon-img"
                  />
                  <img
                    v-else-if="option.value === 'hdfc_bank_team'"
                    src="/icons/hdfc.png"
                    alt=""
                    class="pos-checkout__pay-icon-img"
                  />
                  <span v-else class="pos-checkout__pay-icon" aria-hidden="true">{{ paymentEmoji(option.value) }}</span>
                  <span class="pos-checkout__pay-label">{{ option.label }}</span>
                </button>
              </div>

              <div v-if="cart.length && eligibleDiscountTier" class="pos-preset-banner">
                <div class="pos-preset-banner__info">
                  <span class="pos-preset-banner__dot" aria-hidden="true"></span>
                  <div>
                    <div class="pos-preset-banner__title">
                      Eligible: {{ eligibleDiscountTierLabel }}
                      <button
                        type="button"
                        class="pos-preset-banner__info-btn"
                        aria-label="Show discount chart"
                        title="Discount chart"
                        @click="showDiscountChart = true"
                      >
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="11" x2="12" y2="17" />
                          <line x1="12" y1="7" x2="12.01" y2="7" />
                        </svg>
                      </button>
                    </div>
                    <div class="pos-preset-banner__sub">Saves {{ formatCurrency(eligibleDiscountAmount) }} on this order</div>
                  </div>
                </div>
                <label class="pos-preset-banner__toggle" :class="{ 'is-on': applyPreset }">
                  <input type="checkbox" :checked="applyPreset" @change="onApplyPresetToggle($event.target.checked)" />
                  <span class="pos-preset-banner__track" aria-hidden="true">
                    <span class="pos-preset-banner__thumb"></span>
                  </span>
                  <span class="pos-preset-banner__label">{{ applyPreset ? 'Applied' : 'Apply' }}</span>
                </label>
              </div>

              <div class="pos-checkout__adjusts">
                <div class="pos-checkout__adjust" :class="{ 'is-locked': discountFieldsLocked }">
                  <label>
                    Discount
                    <span v-if="applyPreset" class="pos-preset-pill">Preset applied</span>
                    <span v-else-if="!canOverrideCharges" class="pos-preset-pill pos-preset-pill--muted">Locked</span>
                  </label>
                  <div class="pos-checkout__adjust-control">
                    <select
                      class="pos__payment-mini-mode"
                      :value="discountMode"
                      :disabled="discountFieldsLocked"
                      @change="discountMode = $event.target.value"
                    >
                      <option value="amount">₹</option>
                      <option value="percent">%</option>
                    </select>
                    <input
                      type="number"
                      min="0"
                      class="pos__input pos__input--sm"
                      :value="discountInput"
                      :disabled="discountFieldsLocked"
                      @input="discountInput = Math.max(0, Number($event.target.value || 0))"
                    />
                  </div>
                </div>
                <div class="pos-checkout__adjust" :class="{ 'is-locked': shippingFieldLocked }">
                  <label>
                    Shipping
                    <span v-if="shippingFieldLocked" class="pos-preset-pill pos-preset-pill--muted">Locked</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    class="pos__input pos__input--sm"
                    :value="shippingCharge"
                    :disabled="shippingFieldLocked"
                    @input="shippingCharge = Math.max(0, Number($event.target.value || 0))"
                  />
                </div>
              </div>

              <label class="pos-checkout__gst-toggle">
                <input type="checkbox" :checked="includeGstin" @change="toggleGstin($event.target.checked)" />
                <span>Add GST 5% (B2B bill)</span>
              </label>
              <input
                v-if="includeGstin"
                class="pos__input pos__input--sm pos-checkout__gstin"
                placeholder="GSTIN"
                :value="contact.gstn"
                @input="contact.gstn = String($event.target.value || '').trim().toUpperCase()"
              />
            </section>

            <!-- TOTALS -->
            <section class="pos-checkout__section pos-checkout__totals-section">
              <div class="pos-checkout__totals">
                <div class="pos-checkout__totals-row">
                  <span>Subtotal (incl. GST)</span><span>{{ formatCurrency(cartSubtotal) }}</span>
                </div>
                <div v-if="cartDiscount > 0" class="pos-checkout__totals-row pos-checkout__totals-row--neg">
                  <span>Discount</span><span>−{{ formatCurrency(cartDiscount) }}</span>
                </div>
                <div v-if="Number(shippingCharge) > 0" class="pos-checkout__totals-row">
                  <span>Shipping</span><span>{{ formatCurrency(shippingCharge) }}</span>
                </div>
                <div v-if="bills.length > 1" class="pos-checkout__totals-split">
                  Splits into {{ bills.length }} separate bills
                </div>
              </div>

              <div class="pos-checkout__grand">
                <span>Total Payment</span>
                <span>{{ formatCurrency(cartGrandTotal) }}</span>
              </div>

              <div v-if="cartGst > 0" class="pos-checkout__totals-row pos-checkout__totals-row--note">
                <span>Includes GST (5%)</span><span>{{ formatCurrency(cartGst) }}</span>
              </div>

              <div v-if="paymentMethod === 'cash'" class="pos-checkout__cash">
                <label>Tendered</label>
                <input
                  class="pos__input pos__input--sm pos-checkout__cash-input"
                  type="number"
                  min="0"
                  :value="cashTendered"
                  @input="cashTendered = Math.max(0, Number($event.target.value || 0))"
                />
                <span class="pos-checkout__cash-change" :class="{ 'is-short': changeDue < 0 }">
                  <span>Change</span><strong>{{ formatCurrency(changeDue) }}</strong>
                </span>
              </div>
            </section>
          </div>

          <footer class="pos-checkout__foot">
            <button type="button" class="pos-checkout__cancel" :disabled="isSubmitting" @click="closeCheckout()">Cancel</button>
            <button
              type="button"
              class="pos-checkout__whatsapp"
              :disabled="!canCharge || isSubmitting || contact.phone.length !== 10"
              @click="chargeAndWhatsapp"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.5 3.5A11.5 11.5 0 003.6 19.3L2 22.5l3.3-1.6A11.5 11.5 0 0020.5 3.5zm-8.5 17a9 9 0 01-4.6-1.3l-.3-.2-2 .9.9-1.9-.2-.3a9 9 0 1116.2-5.2 9 9 0 01-10 7.9zm5-6.3c-.3-.1-1.6-.8-1.9-.9s-.4-.1-.6.2-.7.8-.8 1-.3.2-.5.1a7 7 0 01-2-1.3 8 8 0 01-1.4-1.8c-.1-.3 0-.4.1-.5l.4-.5.2-.4a.6.6 0 000-.5l-.8-2c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 00-.7.3 3 3 0 00-1 2.2c0 1.3.9 2.5 1 2.7s1.9 3 4.6 4a8 8 0 002 .5c.6 0 1.3 0 1.8-.4s1.6-1 1.8-1.7v-1.2c0-.1-.3-.2-.5-.3z"/>
              </svg>
              WhatsApp
            </button>
            <button
              type="button"
              class="pos-checkout__charge"
              :disabled="!canCharge || isSubmitting"
              @click="chargeAndPrint"
            >
              {{ isSubmitting ? (progressMessage || 'Saving…') : `Charge ${formatCurrency(cartGrandTotal)} & Print` }}
            </button>
          </footer>
        </div>
      </div>
    </transition>

    <!-- History modal -->
    <transition name="pos-modal">
      <div v-if="historyOpen" class="pos-modal" @click.self="historyOpen = false">
        <div class="pos-modal__panel">
          <header class="pos-modal__head">
            <div>
              <h2>Customer history</h2>
              <p>{{ contact.name || 'Customer' }} · +{{ dialCode }} {{ contact.phone }}</p>
            </div>
            <button type="button" class="pos-modal__close" @click="historyOpen = false" aria-label="Close">×</button>
          </header>

          <div class="pos-modal__body">
            <section v-if="contactCalls.length" class="pos-modal__block">
              <h3>Calls &amp; remarks <span class="pos-modal__count">{{ contactCalls.length }}</span></h3>
              <ul class="pos-modal__list">
                <li v-for="(call, idx) in contactCalls" :key="`mc-${idx}`" class="pos-modal__item">
                  <div class="pos-modal__row">
                    <strong>{{ call.caller || 'Unknown' }}</strong>
                    <span :class="['pos-pill', callStatusClass(call.status)]">{{ call.status }}</span>
                  </div>
                  <div class="pos-modal__date">{{ call.date }}</div>
                  <div v-if="call.comments" class="pos-modal__quote">"{{ call.comments }}"</div>
                </li>
              </ul>
            </section>

            <section v-if="contactOrders.length" class="pos-modal__block">
              <h3>Recent orders <span class="pos-modal__count">{{ contactOrders.length }}</span></h3>
              <ul class="pos-modal__list">
                <li v-for="order in contactOrders" :key="`mo-${order.id}`" class="pos-modal__item">
                  <div class="pos-modal__row">
                    <strong>{{ order.order_number || `#${order.id}` }}</strong>
                    <span class="pos-modal__amt">{{ formatCurrency(order.grand_total) }}</span>
                  </div>
                  <div class="pos-modal__date">
                    {{ formatOrderDate(order.order_date || order.created_at) }} · {{ formatLabel(order.payment_method) }}
                  </div>
                  <ul v-if="order.order_items && order.order_items.length" class="pos-modal__items">
                    <li v-for="item in order.order_items.slice(0,4)" :key="`mi-${item.id}`">
                      {{ item.name }} <span>× {{ item.quantity }}</span>
                    </li>
                  </ul>
                </li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </transition>

    <transition name="sale-success-fade">
      <div v-if="showSuccessModal" class="sale-success" role="dialog" aria-modal="true" aria-labelledby="sale-success-title" @mousedown.self="closeSuccessModal">
        <div class="sale-success__panel" @mousedown.stop>
          <button type="button" class="sale-success__close" aria-label="Close" @click="closeSuccessModal">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <div class="sale-success__animation" aria-hidden="true">
            <span class="sale-success__ring"></span>
            <span class="sale-success__ring sale-success__ring--delay"></span>
            <svg class="sale-success__check" viewBox="0 0 64 64">
              <circle class="sale-success__check-circle" cx="32" cy="32" r="28" />
              <path class="sale-success__check-tick" d="M20 33 L29 42 L46 24" />
            </svg>
          </div>

          <h3 id="sale-success-title" class="sale-success__title">
            {{ lastSavedRequiresApproval ? 'Submitted for Approval' : 'Sale Confirmed!' }}
          </h3>
          <p class="sale-success__subtitle">
            <template v-if="lastSaved && lastSaved.orderIds && lastSaved.orderIds.length > 1">
              {{ lastSaved.orderIds.length }} bills · {{ lastSaved.contactName }}
            </template>
            <template v-else-if="lastSaved && (lastSaved.orderNumber || lastSaved.orderId)">
              Order <strong>#{{ lastSaved.orderNumber || lastSaved.orderId }}</strong> · {{ lastSaved.contactName }}
            </template>
          </p>
          <p v-if="lastSavedRequiresApproval" class="sale-success__notice">
            This order has a discount applied and is held until a privileged user signs off from the Inbox. Invoice + WhatsApp will be available once approved.
          </p>

          <dl v-if="lastSaved" class="sale-success__meta">
            <div class="sale-success__meta-row">
              <dt>Items</dt><dd>{{ lastSaved.itemCount }}</dd>
            </div>
            <div class="sale-success__meta-row">
              <dt>Grand Total</dt><dd>{{ formatCurrency(lastSaved.grandTotal) }}</dd>
            </div>
            <div v-if="lastSaved.contactPhone" class="sale-success__meta-row">
              <dt>Phone</dt><dd>+{{ lastSaved.dialCode || dialCode }} {{ lastSaved.contactPhone }}</dd>
            </div>
          </dl>

          <div class="sale-success__actions">
            <button type="button" :class="['sale-success__btn', 'sale-success__btn--full', accountsCopied ? 'sale-success__btn--copied' : 'sale-success__btn--accounts']" :disabled="!(lastSaved && lastSaved.orderIds && lastSaved.orderIds.length)" @click="copyAccountsMessage">
              <svg v-if="!accountsCopied" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {{ accountsCopied ? 'Copied!' : 'Copy for Accounts Team' }}
            </button>
            <button type="button" class="sale-success__btn sale-success__btn--ghost" :disabled="!(lastSaved && lastSaved.orderIds && lastSaved.orderIds.length) || lastSavedRequiresApproval" @click="downloadInvoice">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download Invoice
            </button>
            <button type="button" class="sale-success__btn sale-success__btn--primary" :disabled="!(lastSaved && lastSaved.contactPhone) || lastSavedRequiresApproval" @click="sendInvoiceWhatsapp">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M20.52 3.48A11.93 11.93 0 0 0 12.04 0C5.5 0 .18 5.32.18 11.86c0 2.09.55 4.13 1.6 5.93L0 24l6.4-1.68a11.86 11.86 0 0 0 5.64 1.44h.01c6.54 0 11.86-5.32 11.86-11.86 0-3.17-1.23-6.15-3.39-8.42zM12.04 21.6h-.01a9.7 9.7 0 0 1-4.95-1.36l-.36-.21-3.8 1 1.02-3.7-.23-.38a9.72 9.72 0 0 1-1.49-5.18c0-5.37 4.37-9.74 9.74-9.74 2.6 0 5.05 1.01 6.89 2.85a9.68 9.68 0 0 1 2.85 6.89c0 5.37-4.37 9.83-9.66 9.83zm5.34-7.27c-.29-.15-1.73-.86-2-.96-.27-.1-.46-.15-.66.15-.2.29-.76.96-.93 1.15-.17.2-.34.22-.63.07-.29-.15-1.24-.46-2.36-1.46-.87-.78-1.46-1.74-1.63-2.03-.17-.29-.02-.45.13-.6.13-.13.29-.34.43-.51.15-.17.2-.29.29-.49.1-.2.05-.37-.02-.51-.07-.15-.66-1.59-.9-2.18-.24-.57-.48-.5-.66-.5l-.56-.01c-.2 0-.51.07-.78.37s-1.03 1.01-1.03 2.46c0 1.45 1.05 2.86 1.2 3.05.15.2 2.08 3.18 5.04 4.46.7.3 1.25.48 1.68.62.71.22 1.35.19 1.86.12.57-.08 1.73-.71 1.98-1.39.24-.68.24-1.27.17-1.39-.07-.12-.27-.2-.56-.34z" />
              </svg>
              Send to Customer
            </button>
          </div>
        </div>
      </div>
    </transition>

    <DiscountChartModal :show.sync="showDiscountChart" />
  </div>
</template>

<script>
import axios from 'axios'
import { getStoredRoles } from '@/auth/access.js'
import { eligibleDiscountBuckets, eligibleDiscountTotal, eligibleDiscountLabel } from '@/utils/discountPresets.js'
import { countryDropdownOptions, dialCodeFor, findCountryByDialCode, DEFAULT_COUNTRY_NAME } from '@/utils/countries.js'
import DiscountChartModal from '@/components/DiscountChartModal.vue'
import AppDropdown from '@/components/AppDropdown.vue'

// Mirrors DiscountPresetController::PRIVILEGED_ROLES on the server.
// Gates both the discount field and the shipping field — only these
// roles may manually edit either value. Everyone else sees the inputs
// locked and applies the eligible-preset toggle instead. Kept in sync
// with TeleSales.vue / OrderForm.vue.
const CHARGE_OVERRIDE_ROLES = ['superadmin', 'admin', 'accountant']

// Only these organisation IDs participate in POS billing. Items and tabs
// for any other organisation are filtered out at bootstrap.
const ALLOWED_ORG_IDS = new Set([1, 2, 4])

// POS only accepts these three payment methods. They are shown to every
// user regardless of role — the role-based filtering in /api/order_form_data
// omits some of these slugs for certain roles, so POS does NOT rely on the
// API to supply them. The fixed list below is the source of truth for both
// the slugs AND the display labels; the API label is only a fallback when a
// local label is missing.
const POS_PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'federal_bank_team', label: 'Fedbank - Team' },
  { value: 'hdfc_bank_team', label: 'HDFC - Team' },
]

function isoDate (value) {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d.getTime())) return String(value)
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
}

export default {
  name: 'POS',
  components: { DiscountChartModal, AppDropdown },
  data () {
    return {
      isLoading: false,
      isSearching: false,
      isSubmitting: false,
      errorMessage: '',
      successMessage: '',
      progressMessage: '',
      searchHint: '',
      productSearch: '',
      showDiscountChart: false,
      productOrgFilter: 'all',
      historyOpen: false,
      checkoutOpen: false,
      editingCustomer: false,
      includeGstin: false,
      // Country dropdown — defaults to India (POS store is in Kerala).
      // Same pattern as OrderForm/TeleSales: dropdown value is the
      // country `name`, and dialCode is derived for E.164 storage.
      // Auto-switches when a pasted number includes a country code.
      countryCode: DEFAULT_COUNTRY_NAME,
      countryOptions: countryDropdownOptions(),
      organisations: [],
      items: [],
      paymentMethodOptions: [],
      shippingMethodOptions: [],
      gstStates: [],
      contact: {
        id: null,
        name: '',
        phone: '',
        address: '',
        pincode: '',
        district: '',
        // The POS store is in Kerala — biller can override per-customer.
        // Backend validator requires a non-empty contact_state.
        state: 'Kerala',
        postofficename: '',
        gstn: '',
      },
      contactCalls: [],
      contactOrders: [],
      cart: [],
      discountMode: 'amount',
      discountInput: 0,
      shippingCharge: 0,
      paymentMethod: 'cash',
      cashTendered: 0,
      discountModeOptions: [
        { value: 'amount', label: '₹' },
        { value: 'percent', label: '%' },
      ],
      // Discount presets — POS spans multiple orgs in a single cart, so
      // we keep tiers indexed by org. Each line resolves its tier from
      // its own org's list. `applyPreset` mirrors TeleSales/OrderForm:
      // when on, the discount input is locked and synced from the sum
      // of per-line eligible discounts.
      presetsByOrg: {},
      applyPreset: false,
      showSuccessModal: false,
      lastSaved: null,
      lastSavedRequiresApproval: false,
      accountsCopied: false,
      accountsCopiedTimer: null,
    }
  },
  computed: {
    filteredItems () {
      const term = String(this.productSearch || '').trim().toLowerCase()
      const orgFilter = this.productOrgFilter
      return this.items.filter((item) => {
        if (orgFilter !== 'all' && String(item.organisation_id) !== String(orgFilter)) {
          return false
        }
        if (!term) return true
        const haystack = [item.name, item.sku, item.shortcode, item.barcode]
          .filter(Boolean)
          .map((value) => String(value).toLowerCase())
          .join(' ')
        return haystack.includes(term)
      }).slice(0, 120)
    },
    cartByOrg () {
      const groups = new Map()
      this.cart.forEach((line) => {
        const key = String(line.organisation_id || 'unknown')
        if (!groups.has(key)) {
          groups.set(key, { organisation_id: line.organisation_id, lines: [], subtotal: 0 })
        }
        const group = groups.get(key)
        group.lines.push(line)
        group.subtotal += Number(line.total || 0)
      })
      return Array.from(groups.values())
    },
    // ── Discount preset wiring ─────────────────────────────────────
    // Resolution per org (one bill = one org); rules live in
    // [src/utils/discountPresets.js]: product-scoped tiers stay
    // per-line and their products are excluded from the org-wide chart;
    // org-wide tiers aggregate qty across the org's uncharted lines only,
    // so a mixed product cart can clear a quantity
    // threshold. Bills get their per-org slice — never a proportional
    // split of a single global discount — so each bill carries the
    // right amount even in a multi-org cart.
    canOverrideCharges () {
      const roles = getStoredRoles().map((r) => String(r).toLowerCase())
      return roles.some((r) => CHARGE_OVERRIDE_ROLES.includes(r))
    },
    discountFieldsLocked () {
      if (this.applyPreset) return true
      return !this.canOverrideCharges
    },
    shippingFieldLocked () {
      return !this.canOverrideCharges
    },
    activeDiscountTiersByOrg () {
      const out = {}
      Object.keys(this.presetsByOrg).forEach((orgId) => {
        const rows = this.presetsByOrg[orgId]
        out[orgId] = (Array.isArray(rows) ? rows : [])
          .filter((t) => t && Boolean(t.active))
          .slice()
          .sort((a, b) => Number(a.min_quantity) - Number(b.min_quantity))
      })
      return out
    },
    eligibleDiscountByOrg () {
      // Bucket the cart by org, then resolve each org independently —
      // one bill per org, so each gets its own threshold/aggregation.
      // Map keyed by stringified org id → { tiers, buckets, amount }.
      const out = {}
      const grouped = new Map()
      for (const line of this.cart) {
        const key = String(line.organisation_id || '')
        if (!key) continue
        if (!grouped.has(key)) grouped.set(key, { orgId: line.organisation_id, lines: [] })
        grouped.get(key).lines.push(line)
      }
      grouped.forEach(({ orgId, lines }, key) => {
        const tiers = this.activeDiscountTiersByOrg[key] || []
        if (!tiers.length) return
        const buckets = eligibleDiscountBuckets(tiers, lines.map((l) => ({
          item_id: l.item_id,
          quantity: l.quantity,
          subtotal: l.total,
        })))
        if (!buckets.length) return
        out[key] = { orgId, tiers, buckets, amount: eligibleDiscountTotal(buckets) }
      })
      return out
    },
    eligibleDiscountBuckets () {
      // Flat list across every org — drives the banner label.
      const all = []
      Object.values(this.eligibleDiscountByOrg).forEach((entry) => { all.push(...entry.buckets) })
      return all
    },
    eligibleDiscountAmount () {
      return Number(Object.values(this.eligibleDiscountByOrg).reduce((acc, entry) => acc + entry.amount, 0).toFixed(2))
    },
    eligibleDiscountAmountByOrg () {
      // Used by `bills` to give each per-org bill its own discount when
      // applyPreset is on — bypasses the proportional split that the
      // manual-discount path uses.
      const map = {}
      Object.entries(this.eligibleDiscountByOrg).forEach(([key, entry]) => { map[key] = entry.amount })
      return map
    },
    eligibleDiscountTier () {
      // Truthy when any org bucket qualifies. Drives the banner v-if.
      return this.eligibleDiscountBuckets[0]?.tier || null
    },
    eligibleDiscountTierLabel () {
      return eligibleDiscountLabel(this.eligibleDiscountBuckets)
    },
    cartSubtotal () {
      return Number(this.cart.reduce((sum, line) => sum + Number(line.total || 0), 0).toFixed(2))
    },
    // Dial code (no leading "+") for the currently selected country.
    // Phones are stored / searched in E.164 form (+<code><subscriber>)
    // so storage stays aligned with the OrderForm / TeleSales convention.
    dialCode () {
      return dialCodeFor(this.countryCode)
    },
    fullContactPhone () {
      return this.contact.phone ? `+${this.dialCode}${this.contact.phone}` : ''
    },
    cartDiscount () {
      if (!this.cartSubtotal) return 0
      // Preset toggle ON: cart-level discount is the sum of per-line
      // eligible discounts. The bills computed then distributes per-org
      // (NOT proportionally to subtotal) so each bill carries the right
      // amount even in a multi-org cart.
      if (this.applyPreset) return this.eligibleDiscountAmount
      if (this.discountMode === 'percent') {
        return Number(((this.cartSubtotal * Number(this.discountInput || 0)) / 100).toFixed(2))
      }
      return Math.min(Number(this.discountInput || 0), this.cartSubtotal)
    },
    cartAfterDiscount () {
      return Number((this.cartSubtotal - this.cartDiscount).toFixed(2))
    },
    cartGst () {
      // Prices are MRP-inclusive of GST; extract for reporting.
      if (!this.includeGstin) return 0
      return Number(((this.cartAfterDiscount * 5) / 105).toFixed(2))
    },
    cartGrandTotal () {
      // GST is already inside cartAfterDiscount — do NOT add cartGst again.
      return Number((this.cartAfterDiscount + Number(this.shippingCharge || 0)).toFixed(2))
    },
    totalItemCount () {
      return this.cart.reduce((sum, line) => sum + Number(line.quantity || 0), 0)
    },
    bills () {
      if (!this.cart.length) return []
      const total = this.cartSubtotal || 1
      const usePresetSplit = this.applyPreset
      const presetByOrg = this.eligibleDiscountAmountByOrg
      return this.cartByOrg.map((group) => {
        const ratio = group.subtotal / total
        // Preset path: each bill carries its org's own eligible
        // discount (so the server's preset validation succeeds).
        // Manual path: split the cart-level discount proportionally
        // to subtotal as before.
        const discount = usePresetSplit
          ? Number((presetByOrg[String(group.organisation_id)] || 0).toFixed(2))
          : Number((this.cartDiscount * ratio).toFixed(2))
        const shipping = Number((Number(this.shippingCharge || 0) * ratio).toFixed(2))
        const afterDiscount = Number((group.subtotal - discount).toFixed(2))
        // Product prices are MRP — already inclusive of GST. Extract the tax
        // for reporting (so the per-bill GST split still gets stored), but
        // do NOT add it to grand_total; that would double-count.
        const gst = this.includeGstin ? Number(((afterDiscount * 5) / 105).toFixed(2)) : 0
        const grand = Number((afterDiscount + shipping).toFixed(2))
        return {
          organisation_id: group.organisation_id,
          organisation_name: this.orgName(group.organisation_id),
          lines: group.lines,
          subtotal: Number(group.subtotal.toFixed(2)),
          discount,
          shipping,
          after_discount: afterDiscount,
          gst,
          grand,
        }
      })
    },
    changeDue () {
      return Number((Number(this.cashTendered || 0) - this.cartGrandTotal).toFixed(2))
    },
    lastCall () {
      return this.contactCalls.length ? this.contactCalls[0] : null
    },
    canCharge () {
      if (!this.cart.length) return false
      if (!this.paymentMethod) return false
      if (!this.contact.name) return false
      // Address is mandatory — invoices need a real delivery line,
      // not the legacy "Store Sale" fallback that previously masked
      // empty addresses on cash counter bills.
      if (!this.contact.address || !this.contact.address.trim()) return false
      return true
    },
    searchHintClass () {
      if (!this.searchHint) return 'is-muted'
      const lower = this.searchHint.toLowerCase()
      if (lower.includes('found')) return 'is-ok'
      if (lower.includes('not')) return 'is-warn'
      return 'is-muted'
    },
  },
  watch: {
    // Keep the locked discount input synced with the eligible amount
    // whenever the cart changes — if the biller adds/removes lines or
    // bumps qty while the toggle is on, the displayed value follows.
    // Auto-disengage when nothing qualifies any more (e.g. cart empty
    // or qty fell below every tier).
    eligibleDiscountAmount (newAmount) {
      if (!this.applyPreset) return
      if (newAmount <= 0) {
        this.applyPreset = false
        this.discountInput = 0
        return
      }
      this.discountInput = newAmount
    },
  },
  created () {
    this.loadBootstrap()
  },
  mounted () {
    this.$nextTick(() => {
      if (this.$refs.searchInput) this.$refs.searchInput.focus()
    })
  },
  methods: {
    openCheckout () {
      if (!this.cart.length) return
      this.checkoutOpen = true
    },
    closeCheckout () {
      this.checkoutOpen = false
    },
    loadBootstrap () {
      this.isLoading = true
      this.errorMessage = ''

      axios.get(`${this.$apiAdress}/api/order_form_data?cross_org=1`, { timeout: 20000 })
        .then((response) => {
          const data = response.data || {}
          const dropdown = data.drop_down_items || {}

          const apiPaymentLabels = new Map(
            this.normalizeDropdown(dropdown.payment_method)
              .map((option) => [String(option.value).toLowerCase(), option.label])
          )
          this.paymentMethodOptions = POS_PAYMENT_METHODS.map((method) => ({
            value: method.value,
            label: method.label || apiPaymentLabels.get(method.value),
          }))
          this.shippingMethodOptions = this.normalizeDropdown(dropdown.shipping_method)

          if (this.paymentMethodOptions.length && !this.paymentMethodOptions.find((option) => option.value === this.paymentMethod)) {
            this.paymentMethod = this.paymentMethodOptions[0].value
          }

          const items = Array.isArray(data.items) ? data.items : []
          this.items = items
            .map((item) => ({
              id: Number(item.id),
              organisation_id: Number(item.organisation_id || 1),
              name: item.name,
              description: item.description || '',
              sku: item.sku || '',
              shortcode: item.shortcode || '',
              barcode: item.barcode || '',
              image: item.image || '',
              price: Number(item.selling_price ?? item.price ?? item.sale_price ?? item.rate ?? item.unit_price ?? item.mrp ?? 0),
            }))
            .filter((item) => ALLOWED_ORG_IDS.has(item.organisation_id))

          const orgs = Array.isArray(data.organisations) ? data.organisations : []
          this.organisations = orgs
            .map((org) => ({ id: Number(org.id), name: org.name }))
            .filter((org) => ALLOWED_ORG_IDS.has(org.id))

          const gstStateGroups = data.gst_states || {}
          const groupedValues = Array.isArray(gstStateGroups) ? gstStateGroups : Object.values(gstStateGroups)
          this.gstStates = groupedValues.flatMap((rows) => (Array.isArray(rows) ? rows : []))
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = 'Unable to load POS catalogue.'
        })
        .finally(() => {
          this.isLoading = false
        })
      // Fire preset fetches in parallel with the catalogue load — they
      // don't block the UI and a missing-presets failure should never
      // break POS itself, so we don't await or surface errors here.
      this.loadDiscountPresets()
    },
    loadDiscountPresets () {
      const presetsByOrg = {}
      ALLOWED_ORG_IDS.forEach((orgId) => { presetsByOrg[orgId] = [] })
      this.presetsByOrg = presetsByOrg
      const requests = Array.from(ALLOWED_ORG_IDS).map((orgId) => {
        return axios.get(`${this.$apiAdress}/api/discount-presets/by-org/${orgId}`, { timeout: 10000 })
          .then((response) => {
            this.$set(this.presetsByOrg, orgId, Array.isArray(response.data) ? response.data : [])
          })
          .catch(() => {
            // Silent: missing presets for an org just means no toggle
            // shows for items from that org. POS still works.
            this.$set(this.presetsByOrg, orgId, [])
          })
      })
      return Promise.all(requests)
    },
    normalizeDropdown (entries) {
      return (entries || []).map((entry) => ({
        value: String(entry.slug || entry.value || entry.id || ''),
        label: entry.name || entry.text || entry.slug || '',
      })).filter((entry) => entry.value)
    },
    orgItemCount (orgId) {
      return this.items.filter((item) => Number(item.organisation_id) === Number(orgId)).length
    },
    orgName (orgId) {
      const found = this.organisations.find((org) => Number(org.id) === Number(orgId))
      return found ? found.name : `Org ${orgId}`
    },
    orgShort (orgId) {
      const name = this.orgName(orgId)
      if (!name) return ''
      const lower = name.toLowerCase()
      if (lower.includes('agro')) return 'AGRO'
      if (lower.includes('drug')) return 'DRUGS'
      const parts = name.split(/\s+/).filter(Boolean)
      return parts.length === 1 ? parts[0].slice(0, 4).toUpperCase() : parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase()
    },
    orgPillClass (orgId) {
      const name = this.orgName(orgId).toLowerCase()
      if (name.includes('agro')) return 'is-agro'
      if (name.includes('drug')) return 'is-drugs'
      return 'is-default'
    },
    orgLogo (name) {
      const lower = String(name || '').toLowerCase()
      if (lower.includes('agro')) return '/MCCI-Agro.png'
      if (lower.includes('drug')) return '/MCCI-Drugs.png'
      return ''
    },
    tileSubtitle (item) {
      if (item.description) {
        return item.description
      }
      return `${this.orgName(item.organisation_id)} · ${item.sku || item.shortcode || 'product'}`
    },
    setPhone (value) {
      this.contact.phone = this.normalizePhoneInput(value, { syncCountry: true })
    },
    normalizePhoneInput (value, { syncCountry = false } = {}) {
      // Strip non-digits, then handle pasted numbers that include a
      // country-code prefix (e.g. "+919207948134", "+966500000000").
      // Peel the matching dial code so the field shows only the
      // local subscriber number, and — when syncCountry is on —
      // switch the country dropdown to match so staff don't have to
      // re-select it manually. Kept in sync with OrderForm/TeleSales.
      //
      // Trigger conditions: explicit "+" prefix in the raw input
      // (clear paste signal) OR more than 10 digits (a typed Indian
      // number can't exceed 10, so the excess is almost always a
      // pasted country code). Falls back to the last 10 digits when
      // no known country code matches, preserving recovery for
      // common malformed pastes (0-leader, ISD code, etc.).
      const raw = String(value || '')
      let digits = raw.replace(/\D/g, '')

      const shouldPeel = digits.length > 10 || raw.trim().startsWith('+')

      if (shouldPeel) {
        const match = findCountryByDialCode(digits)
        if (match) {
          const codeDigits = match.code.replace(/^\+/, '')
          digits = digits.slice(codeDigits.length)
          if (syncCountry && this.countryCode !== match.name) {
            this.countryCode = match.name
          }
        } else if (digits.length > 10) {
          digits = digits.slice(-10)
        }
      }

      return digits.slice(0, 10)
    },
    toggleGstin (checked) {
      this.includeGstin = Boolean(checked)
      if (!this.includeGstin) this.contact.gstn = ''
    },
    resetCustomer () {
      this.contact = {
        id: null,
        name: '',
        phone: '',
        address: '',
        pincode: '',
        district: '',
        // Default state for the Kerala-based POS store — keeps the
        // contact_state validator happy on new bills.
        state: 'Kerala',
        postofficename: '',
        gstn: '',
      }
      this.contactCalls = []
      this.contactOrders = []
      this.searchHint = ''
      this.editingCustomer = false
      // Snap the country dropdown back to the default so the next
      // customer's auto-detect can run from a clean slate.
      this.countryCode = DEFAULT_COUNTRY_NAME
    },
    searchCustomer () {
      if (this.contact.phone.length !== 10) {
        this.searchHint = 'Enter a valid 10-digit phone number.'
        return
      }
      this.isSearching = true
      this.searchHint = 'Searching…'

      axios.get(`${this.$apiAdress}/api/contacts/phone/${encodeURIComponent(this.fullContactPhone)}?cross_org=1`, { timeout: 15000 })
        .then((response) => {
          const data = response.data || {}
          const contact = Array.isArray(data.contact) ? data.contact[0] : null
          const orders = Array.isArray(data.orders) ? data.orders : []
          this.contactCalls = Array.isArray(data.calls) ? data.calls : []
          this.contactOrders = orders

          if (!contact) {
            this.searchHint = 'New customer — add details below.'
            this.contact.id = null
            this.editingCustomer = true
            return
          }

          this.contact.id = contact.id || null
          this.contact.name = contact.name || this.contact.name

          // Address hydration mirrors OrderForm/TeleSales: prefer the
          // primary contact-address row, but fall back to the latest
          // order when the contact has no addresses row or its row is
          // stale/empty. Without this fallback, customers created via
          // older flows show no address even though their last order
          // shipped to a real one.
          const addresses = Array.isArray(contact.address) ? contact.address : []
          const primary = addresses.find((address) => Number(address.pincode || 0) > 0) || addresses[0] || null
          const latestOrder = orders[0] || null

          const resolvedAddress = (primary && primary.address) || (latestOrder && latestOrder.contact_address) || ''
          const resolvedPincode = (primary && primary.pincode) || (latestOrder && latestOrder.contact_pincode) || ''
          const resolvedPostOffice = (primary && primary.post_office_name) || (latestOrder && latestOrder.contact_postofficename) || ''
          const resolvedDistrict = (primary && primary.district) || (latestOrder && latestOrder.contact_district) || ''
          const resolvedState = (primary && primary.state) || (latestOrder && latestOrder.contact_state) || ''
          const resolvedGstin = (primary && primary.gstin) || (latestOrder && latestOrder.contact_gstn) || ''

          if (resolvedAddress || resolvedPincode || resolvedPostOffice) {
            this.contact.address = resolvedAddress || this.contact.address
            this.contact.pincode = resolvedPincode ? String(resolvedPincode) : this.contact.pincode
            this.contact.postofficename = resolvedPostOffice || this.contact.postofficename
            this.contact.district = resolvedDistrict || this.contact.district
            this.contact.state = resolvedState || this.contact.state
          }
          if (resolvedGstin) {
            this.contact.gstn = resolvedGstin
            this.includeGstin = true
          }
          this.searchHint = 'Customer found.'
        })
        .catch((error) => {
          console.log(error)
          this.searchHint = 'Lookup failed — please try again.'
        })
        .finally(() => {
          this.isSearching = false
        })
    },
    onSearchEnter () {
      const term = String(this.productSearch || '').trim().toLowerCase()
      if (!term) return
      const exact = this.items.find((item) =>
        String(item.sku || '').toLowerCase() === term ||
        String(item.shortcode || '').toLowerCase() === term ||
        String(item.barcode || '').toLowerCase() === term
      )
      if (exact) {
        this.addItemToCart(exact)
        this.productSearch = ''
        return
      }
      if (this.filteredItems.length === 1) {
        this.addItemToCart(this.filteredItems[0])
        this.productSearch = ''
      }
    },
    addItemToCart (item) {
      const cartKey = `${item.organisation_id}-${item.id}`
      const existing = this.cart.find((line) => line.cart_key === cartKey)
      if (existing) {
        existing.quantity += 1
        existing.total = Number((existing.quantity * existing.price).toFixed(2))
        return
      }
      this.cart.push({
        cart_key: cartKey,
        item_id: item.id,
        organisation_id: item.organisation_id,
        name: item.name,
        sku: item.sku,
        shortcode: item.shortcode,
        image: item.image || '',
        price: Number(item.price || 0),
        quantity: 1,
        total: Number((item.price || 0).toFixed(2)),
      })
    },
    incQty (line) {
      line.quantity = Number(line.quantity || 0) + 1
      line.total = Number((line.quantity * line.price).toFixed(2))
    },
    decQty (line) {
      const next = Math.max(1, Number(line.quantity || 1) - 1)
      line.quantity = next
      line.total = Number((next * line.price).toFixed(2))
    },
    setQty (line, value) {
      const qty = Math.max(1, Number(value || 1))
      line.quantity = qty
      line.total = Number((qty * line.price).toFixed(2))
    },
    removeLine (line) {
      this.cart = this.cart.filter((l) => l !== line)
    },
    callStatusClass (status) {
      const s = String(status || '').toLowerCase()
      if (s.includes('sold') || s === 'closed') return 'is-success'
      if (s.includes('not interested')) return 'is-muted'
      if (s.includes('not attended') || s.includes('no response')) return 'is-warn'
      if (s.includes('priority')) return 'is-info'
      return 'is-default'
    },
    formatCurrency (value) {
      const amount = Number(value || 0)
      return Number.isFinite(amount) ? `₹${amount.toFixed(2)}` : '₹0.00'
    },
    formatLabel (value) {
      return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },
    paymentEmoji (value) {
      const map = { cash: '💵', card: '💳' }
      return map[String(value).toLowerCase()] || '💳'
    },
    formatOrderDate (value) {
      return isoDate(value)
    },
    selectedGstStateFor (state) {
      // Match space-insensitively so "TAMILNADU" resolves to the
      // "Tamil Nadu" gst_states row (otherwise it falls through to the
      // Kerala fallback and an inter-state order gets taxed as intra-state).
      const normalise = (value) => String(value || '').replace(/\s+/g, '').toLowerCase()
      const target = normalise(state)
      return this.gstStates.find((entry) => normalise(entry.state) === target) || null
    },
    buildPayload (bill) {
      const customerName = this.contact.name || 'Customer'
      // Walk-in / unidentified customers still need a syntactically valid
      // contact_phone (backend validator is required|string). Keep the
      // legacy 10-zero sentinel for that case; real numbers are stored in
      // E.164 form so search/storage stays consistent across the system.
      const customerPhone = this.contact.phone ? this.fullContactPhone : '0000000000'
      // Address is enforced by `canCharge` before this is reached, so
      // the legacy "Store Sale" fallback is gone — keep a trimmed
      // string only.
      const customerAddress = String(this.contact.address || '').trim()
      const customerPincode = this.contact.pincode || 0
      const customerPostoffice = this.contact.postofficename || 'NA'
      // contact_district & contact_state are required|string on the backend
      // and Laravel's `required` rejects whitespace-only strings. Trim before
      // checking and fall back to the Kerala POS store's locality when blank.
      const trimmedDistrict = String(this.contact.district || '').trim()
      const trimmedState = String(this.contact.state || '').trim()
      const customerDistrict = trimmedDistrict || 'Malappuram'
      const customerState = trimmedState || 'Kerala'
      const gstState = this.selectedGstStateFor(customerState)
      const stateCode = gstState ? String(gstState.state_code || '').toUpperCase() : ''
      const gstStin = gstState ? gstState.state_tin : null

      let cgst = 0, sgst = 0, igst = 0
      if (bill.gst > 0) {
        if (stateCode === 'KL' || !stateCode) {
          cgst = Number((bill.gst / 2).toFixed(2))
          sgst = Number((bill.gst / 2).toFixed(2))
        } else {
          igst = bill.gst
        }
      }

      const weight = bill.lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0) * 0.201

      // Credit the telesales staff who last contacted this customer. The
      // POS biller (a storekeeper) becomes user_id on the order, while
      // care_of tracks the telesales agent whose call brought the customer
      // in — used downstream for incentive reporting. care_of is now
      // resolved authoritatively on the server at create time: it's the
      // most recent call placed by a tele caller in the ORDER's product
      // organisation, so credit follows the product line even when a later
      // call came from another org's desk. We no longer send a cross-org
      // guess from here.

      return {
        contact_id: this.contact.id || undefined,
        contact_name: customerName,
        contact_address: customerAddress,
        contact_phone: customerPhone,
        contact_phone_2: customerPhone,
        contact_pincode: customerPincode,
        contact_postofficename: customerPostoffice,
        contact_district: customerDistrict,
        contact_state: customerState,
        contact_gstn: this.includeGstin && this.contact.gstn ? this.contact.gstn : null,
        country: this.countryCode || 'India',
        city: null,
        shipping_type: 'domestic',
        local_body_name: null,
        local_body_type_name: null,
        ward_no: 0,
        billing_name: null,
        billing_address: null,
        billing_pincode: null,
        billing_postofficename: null,
        billing_district: null,
        billing_state: null,
        order_type: 'store_sale',
        // Total bottles = sum of line quantities, not the number of product
        // lines — feeds the "Bottles Sold" tiles, leaderboard and exports.
        item_count: bill.lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0),
        weight: Number(weight.toFixed(3)),
        total: bill.subtotal,
        discount: bill.discount,
        total_after_discount: bill.after_discount,
        shipping_charge: bill.shipping,
        cod_charge: 0,
        total_shipping_charge: bill.shipping,
        grand_total: bill.grand,
        gst_percentage: this.includeGstin ? 5 : 0,
        cgst_amt: cgst,
        sgst_amt: sgst,
        igst_amt: igst,
        gst_amt: bill.gst,
        gst_state_code: stateCode,
        gst_stin: gstStin,
        payment_method: this.paymentMethod,
        payment_status: 'payment_received',
        order_status: 'confirmed',
        payment_date: new Date().toISOString().slice(0, 10),
        shipping_method: 'direct',
        shipping_status: 'shipped',
        order_items: bill.lines.map((line) => ({
          item_id: Number(line.item_id),
          name: line.name,
          sku: line.sku,
          shortcode: line.shortcode,
          quantity: Number(line.quantity),
          price: Number(line.price),
          total: Number(line.total),
        })),
        crops_data: [],
        is_call: false,
        telesales_form: false,
      }
    },
    async submitBills () {
      // Cross-org carts split into one POST per organisation. Fire them in
      // parallel — different orgs use different document-series locks, so
      // they don't contend on the server. We also skip the GET-after-POST
      // (the Blade invoice popup loads the order by ID and renders the
      // invoice number itself, so the client doesn't need to refetch it).
      this.progressMessage = this.bills.length > 1
        ? `Saving ${this.bills.length} bills…`
        : 'Saving…'

      const requests = this.bills.map((bill) => {
        const payload = this.buildPayload(bill)
        return axios.post(`${this.$apiAdress}/api/orders`, payload, { timeout: 30000 })
          .then((response) => {
            const data = response?.data || {}
            // Three failure shapes the backend can return with HTTP 200:
            //   1. Validation: { error: { field: [msg] }, ... }
            //   2. Exception in store(): { message, type: 'error' }
            //   3. Anything that doesn't return an OrderId is also a failure.
            if (data.error) {
              const errText = typeof data.error === 'string' ? data.error : JSON.stringify(data.error)
              throw new Error(errText)
            }
            if (data.type === 'error') {
              throw new Error(data.message || 'Server error while saving the bill.')
            }
            if (!data.OrderId) {
              throw new Error(data.message || 'Bill was not saved — server returned no order id.')
            }
            return { bill, orderId: data.OrderId, orderNumber: data.order_number || '', requiresApproval: Boolean(data.requires_approval) }
          })
      })

      return Promise.all(requests)
    },
    chargeAndPrint () {
      if (!this.canCharge) return
      // Open the invoice window synchronously inside the click handler so
      // the browser's popup blocker honours it. We navigate to the real
      // URL once the save returns the order IDs.
      const invoiceWin = window.open('about:blank', '_blank')
      this.runSaveFlow(invoiceWin, null)
    },
    chargeAndWhatsapp () {
      if (!this.canCharge || this.contact.phone.length !== 10) return
      // Pre-build the WhatsApp message from the current cart (no need to
      // wait for the save — we already have everything we need locally)
      // and open BOTH popups synchronously so neither gets blocked.
      const message = this.buildWhatsappMessage()
      const waUrl = `https://wa.me/${this.dialCode}${this.contact.phone}?text=${encodeURIComponent(message)}`
      const invoiceWin = window.open('about:blank', '_blank')
      const waWin = window.open(waUrl, '_blank', 'noopener')
      this.runSaveFlow(invoiceWin, waWin)
    },
    async runSaveFlow (invoiceWin, waWin) {
      this.errorMessage = ''
      this.successMessage = ''
      this.isSubmitting = true
      try {
        const savedBills = await this.submitBills()
        const orderIds = savedBills.map((entry) => entry.orderId).filter(Boolean)
        const orderNumbers = savedBills.map((entry) => entry.orderNumber).filter(Boolean)
        const requiresApproval = savedBills.some((entry) => entry.requiresApproval)

        // Snapshot the cart BEFORE resetAll() wipes it — the success
        // modal renders item count / grand total / phone from the
        // snapshot, not from live form state.
        this.lastSaved = this.buildSaleSnapshot(orderIds, orderNumbers)
        this.lastSavedRequiresApproval = requiresApproval

        if (requiresApproval) {
          // Discount-gated: invoice + WhatsApp aren't valid until a
          // privileged user signs off via the Inbox. Close the popups
          // the click handler opened pre-emptively so the biller isn't
          // staring at a blank tab or a WhatsApp draft referencing an
          // unconfirmed order.
          if (invoiceWin) { try { invoiceWin.close() } catch (e) { /* ignore */ } }
          if (waWin) { try { waWin.close() } catch (e) { /* ignore */ } }
        } else {
          if (orderIds.length && invoiceWin) {
            const url = `${this.$apiAdress}/invoices?orders=${orderIds.join(',')}&autoprint=1`
            invoiceWin.location.href = url
          } else if (orderIds.length && !invoiceWin) {
            this.errorMessage = 'Pop-up blocked — please allow pop-ups to download the invoice.'
          }
        }

        this.checkoutOpen = false
        this.showSuccessModal = true
        this.resetAll(true)
      } catch (error) {
        console.log(error)
        this.errorMessage = error?.message || 'Unable to save bill.'
        // Save failed — close the blank invoice tab so the biller isn't
        // staring at about:blank. The WhatsApp tab is left open: the
        // biller can decide whether to send or close it.
        if (invoiceWin) {
          try { invoiceWin.close() } catch (e) { /* ignore */ }
        }
      } finally {
        this.isSubmitting = false
        this.progressMessage = ''
      }
    },
    buildSaleSnapshot (orderIds, orderNumbers) {
      const ids = Array.isArray(orderIds) ? orderIds.filter(Boolean) : []
      const numbers = Array.isArray(orderNumbers) ? orderNumbers.filter(Boolean) : []
      return {
        orderId: ids[0] || null,
        orderIds: ids,
        orderNumber: numbers[0] || null,
        orderNumbers: numbers,
        contactName: this.contact.name || 'Walk-in Customer',
        contactPhone: this.contact.phone || '',
        // Snapshot the dial code so the success modal / WhatsApp links
        // render the correct country even after the form resets.
        dialCode: this.dialCode,
        countryName: this.countryCode,
        itemCount: this.totalItemCount,
        grandTotal: this.cartGrandTotal,
        discount: this.cartDiscount,
        shipping: Number(this.shippingCharge || 0),
        gst: this.cartGst,
        paymentMethod: this.paymentMethod,
        branchName: this.currentUserBranchName(),
        organisationName: this.$store.getters.activeOrganisationDisplayName || '',
        bills: this.bills.map((b) => ({
          organisation_name: b.organisation_name,
          lines: b.lines.map((line) => ({
            name: line.name,
            quantity: Number(line.quantity || 0),
            total: Number(line.total || 0),
          })),
          discount: Number(b.discount || 0),
          shipping: Number(b.shipping || 0),
          gst: Number(b.gst || 0),
          grand: Number(b.grand || 0),
        })),
      }
    },
    closeSuccessModal () {
      this.showSuccessModal = false
      this.lastSaved = null
      this.lastSavedRequiresApproval = false
      if (this.accountsCopiedTimer) {
        clearTimeout(this.accountsCopiedTimer)
        this.accountsCopiedTimer = null
      }
      this.accountsCopied = false
    },
    currentUserBranchName () {
      try {
        const raw = localStorage.getItem('auth_user')
        if (!raw) return ''
        const user = JSON.parse(raw)
        const candidates = [
          user?.branch?.name,
          user?.branch_name,
          typeof user?.branch === 'string' ? user.branch : null,
        ]
        const found = candidates.find((v) => typeof v === 'string' && v.trim().length > 0)
        return found ? found.trim() : ''
      } catch (e) {
        return ''
      }
    },
    buildAccountsMessage () {
      const snap = this.lastSaved
      if (!snap) return ''
      const lines = []
      if (snap.branchName) lines.push(`${snap.branchName}`)
      if (snap.organisationName) lines.push(String(snap.organisationName))
      const labels = Array.isArray(snap.orderNumbers) && snap.orderNumbers.length
        ? snap.orderNumbers
        : (Array.isArray(snap.orderIds) && snap.orderIds.length
          ? snap.orderIds
          : (snap.orderNumber ? [snap.orderNumber] : (snap.orderId ? [snap.orderId] : [])))
      labels.forEach((label) => lines.push(String(label)))
      if (snap.contactName) lines.push(String(snap.contactName))
      // Surface the products + total quantity, matching the TeleSales
      // accounts message. POS can span multiple orgs in one sale, so the
      // bill lines are flattened across every bill in the snapshot.
      const saleLines = Array.isArray(snap.bills)
        ? snap.bills.flatMap((bill) => (Array.isArray(bill.lines) ? bill.lines : []))
        : []
      const totalQty = saleLines.reduce((sum, line) => sum + Number(line.quantity || 0), 0)
      const productNames = saleLines
        .map((line) => String(line.name || '').trim())
        .filter(Boolean)
      if (productNames.length) {
        lines.push(`Product quantity ${totalQty}`)
        lines.push('')
        lines.push('Product name :')
        productNames.forEach((name) => lines.push(name))
        lines.push('')
      }
      lines.push('Payment received')
      const method = String(snap.paymentMethod || '').toUpperCase() || 'PAYMENT'
      const amount = Math.round(Number(snap.grandTotal || 0))
      lines.push(`${method}: ${amount}`)
      const discountAmt = Number(snap.discount || 0)
      if (discountAmt > 0) {
        lines.push(`Discount: ${Math.round(discountAmt)}`)
        // Also surface the discount as a percentage of the subtotal, to
        // match the TeleSales accounts message. Subtotal isn't snapshotted
        // directly, but grand_total = (subtotal − discount) + shipping
        // (GST is MRP-inclusive, never added), so subtotal back-solves to
        // grandTotal − shipping + discount.
        const subtotal = Number(snap.grandTotal || 0) - Number(snap.shipping || 0) + discountAmt
        if (subtotal > 0) {
          const discountPercent = Number(((discountAmt / subtotal) * 100).toFixed(2))
          lines.push(`Proposed discount: ${discountPercent}%`)
        }
      }
      const phone = String(snap.contactPhone || '').replace(/\D/g, '')
      const code = snap.dialCode || this.dialCode
      if (phone) lines.push(`+${code}${phone}`)
      return lines.join('\n')
    },
    copyAccountsMessage () {
      if (!this.lastSaved) return
      const text = this.buildAccountsMessage()
      if (!text) return
      const markCopied = () => {
        this.accountsCopied = true
        if (this.accountsCopiedTimer) clearTimeout(this.accountsCopiedTimer)
        this.accountsCopiedTimer = setTimeout(() => {
          this.accountsCopied = false
          this.accountsCopiedTimer = null
        }, 2500)
      }
      const fallback = () => {
        try {
          const ta = document.createElement('textarea')
          ta.value = text
          ta.setAttribute('readonly', '')
          ta.style.position = 'fixed'
          ta.style.top = '-9999px'
          document.body.appendChild(ta)
          ta.select()
          ta.setSelectionRange(0, text.length)
          const ok = document.execCommand('copy')
          document.body.removeChild(ta)
          if (ok) markCopied()
        } catch (e) { /* clipboard unavailable */ }
      }
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(markCopied).catch(fallback)
      } else {
        fallback()
      }
    },
    invoiceUrl () {
      const ids = this.lastSaved && this.lastSaved.orderIds ? this.lastSaved.orderIds : []
      if (!ids.length) return ''
      return `${this.$apiAdress}/invoices?orders=${ids.join(',')}`
    },
    downloadInvoice () {
      const url = this.invoiceUrl()
      if (!url) return
      window.open(`${url}&autoprint=1`, '_blank', 'noopener')
    },
    buildSaleWhatsappMessage () {
      const snap = this.lastSaved
      if (!snap) return ''
      const customer = snap.contactName || 'Customer'
      const lines = [`Hello ${customer},`, '', 'Thank you for your purchase. Receipt:']
      snap.bills.forEach((bill) => {
        lines.push('')
        lines.push(`*${bill.organisation_name}*`)
        bill.lines.forEach((line) => {
          lines.push(`${line.quantity} × ${line.name} — ${this.formatCurrency(line.total)}`)
        })
        if (bill.discount > 0) lines.push(`Discount: −${this.formatCurrency(bill.discount)}`)
        if (bill.shipping > 0) lines.push(`Shipping: ${this.formatCurrency(bill.shipping)}`)
        lines.push(`*Total: ${this.formatCurrency(bill.grand)}*`)
        if (bill.gst > 0) lines.push(`(Includes GST: ${this.formatCurrency(bill.gst)})`)
      })
      lines.push('')
      if (snap.paymentMethod) lines.push(`Payment: ${this.formatLabel(snap.paymentMethod)}`)
      const invoice = this.invoiceUrl()
      if (invoice) {
        lines.push('')
        lines.push(`Invoice: ${invoice}`)
      }
      return lines.join('\n')
    },
    sendInvoiceWhatsapp () {
      const snap = this.lastSaved
      if (!snap || !snap.contactPhone) return
      const phone = String(snap.contactPhone).replace(/\D/g, '')
      const code = snap.dialCode || this.dialCode
      const message = this.buildSaleWhatsappMessage()
      const waUrl = `https://wa.me/${code}${phone}?text=${encodeURIComponent(message)}`
      window.open(waUrl, '_blank', 'noopener')
    },
    buildWhatsappMessage () {
      const customer = this.contact.name || 'Customer'
      const lines = [`Hello ${customer},`, '', 'Thank you for your purchase. Receipt:']
      this.bills.forEach((bill) => {
        lines.push('')
        lines.push(`*${bill.organisation_name}*`)
        bill.lines.forEach((line) => {
          lines.push(`${line.quantity} × ${line.name} — ${this.formatCurrency(line.total)}`)
        })
        if (bill.discount > 0) lines.push(`Discount: −${this.formatCurrency(bill.discount)}`)
        if (bill.shipping > 0) lines.push(`Shipping: ${this.formatCurrency(bill.shipping)}`)
        lines.push(`*Total: ${this.formatCurrency(bill.grand)}*`)
        if (bill.gst > 0) lines.push(`(Includes GST: ${this.formatCurrency(bill.gst)})`)
      })
      lines.push('')
      lines.push(`Payment: ${this.formatLabel(this.paymentMethod)}`)
      lines.push('Thank you for visiting our store!')
      return lines.join('\n')
    },
    onApplyPresetToggle (checked) {
      this.applyPreset = Boolean(checked)
      if (this.applyPreset) {
        // Reflect the applied amount in the locked input so the biller
        // still sees what's being charged. cartDiscount short-circuits
        // to eligibleDiscountAmount in preset mode, but mirroring into
        // discountInput keeps the field's display honest.
        this.discountMode = 'amount'
        this.discountInput = this.eligibleDiscountAmount
      } else {
        this.discountMode = 'amount'
        this.discountInput = 0
      }
    },
    resetAll (keepCustomer = false) {
      this.cart = []
      this.discountInput = 0
      this.shippingCharge = 0
      this.cashTendered = 0
      this.applyPreset = false
      this.productSearch = ''
      this.productOrgFilter = 'all'
      if (!keepCustomer) {
        this.resetCustomer()
        this.includeGstin = false
      }
      this.$nextTick(() => {
        if (this.$refs.searchInput) this.$refs.searchInput.focus()
      })
    },
  },
}
</script>

<style scoped>
/* ─────────────────────────────────────────────────────────────
   Layout: reference-inspired POS. Catalogue with tabs on the
   left, persistent cart panel on the right.
   ───────────────────────────────────────────────────────────── */
.pos {
  --pos-bg: #f5f7fb;
  --pos-card: #ffffff;
  --pos-border: #e5e9f2;
  --pos-border-soft: #eef1f7;
  --pos-text: #1f2a44;
  --pos-muted: #6b7280;
  --pos-soft: #94a3b8;
  --pos-brand: #0e5b46;
  --pos-brand-2: #16a07a;
  --pos-brand-gradient: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  --pos-cart-width: 380px;

  display: grid;
  grid-template-columns: minmax(0, 1fr) var(--pos-cart-width);
  grid-template-rows: 100vh;
  height: 100vh;
  background: var(--pos-bg);
}

/* Suppress the browser's default focus ring on clickable elements
   inside the POS — each interactive element supplies its own visible
   state (active tab underline, hover lift, etc.). Inputs keep their
   custom brand-green ring via their own :focus rules. */
.pos button:focus,
.pos a:focus,
.pos [role="button"]:focus,
.pos__tile-media:focus,
.pos__tile:focus,
.pos select:focus {
  outline: none;
}

.pos button::-moz-focus-inner {
  border: 0;
}

.pos__left {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  min-height: 0;
  /* Without min-width:0 the grid item's content-based min size can
     push the parent grid wider than the viewport (long product
     names / SKUs in the tiles below). */
  min-width: 0;
  padding: 0.7rem 0.9rem 0.7rem 0.9rem;
}

/* ── Top bar (search + refresh + ledger shortcut) ── */
.pos__topbar {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 0.6rem;
  align-items: center;
}

.pos__search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid var(--pos-border);
  border-radius: 10px;
  padding: 0 12px 0 40px;
  height: 40px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  transition: border-color .12s, box-shadow .12s;
}

.pos__search-wrap:focus-within {
  border-color: var(--pos-brand-2);
  box-shadow: 0 0 0 4px rgba(22, 160, 122, 0.16);
}

.pos__search-icon {
  position: absolute;
  left: 13px;
  width: 16px;
  height: 16px;
  color: var(--pos-soft);
}

.pos__search {
  flex: 1 1 auto;
  border: 0;
  outline: 0;
  background: transparent;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--pos-text);
}

.pos__search::placeholder {
  color: var(--pos-soft);
  font-weight: 400;
}

.pos__search-clear {
  cursor: pointer;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #f1f4f9;
  color: var(--pos-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  line-height: 1;
}

.pos__search-clear:hover {
  background: #e2e7ef;
  color: var(--pos-text);
}

.pos__refresh {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 0.85rem;
  height: 40px;
  background: #fff;
  border: 1px solid var(--pos-border);
  border-radius: 10px;
  color: var(--pos-text);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color .12s, color .12s;
}

.pos__refresh:hover:not(:disabled) {
  border-color: var(--pos-brand-2);
  color: var(--pos-brand);
}

.pos__refresh .is-spinning { animation: pos-spin 1s linear infinite; }
@keyframes pos-spin { to { transform: rotate(360deg); } }

/* Ledger shortcut — navigates to /pos-sales. Icon-only, square, same
   visual weight as the refresh button. */
.pos__ledger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #fff;
  border: 1px solid var(--pos-border);
  border-radius: 10px;
  color: var(--pos-text);
  cursor: pointer;
  transition: border-color .12s, color .12s, background .12s, transform .12s;
}

.pos__ledger:hover {
  border-color: var(--pos-brand-2);
  color: var(--pos-brand);
  background: #f5fbf8;
  transform: translateY(-1px);
}

.pos__ledger:active {
  transform: translateY(0);
}

.pos__alert {
  grid-column: 1 / -1;
  margin: 0;
  border-radius: 10px;
  font-weight: 600;
}

.pos__alert--ok {
  background: #dff5ea;
  color: #0e5b46;
  border-color: #b8e4ce;
}

/* ── Tabs (reference-style icon row) ── */
.pos__tabs {
  flex: 0 0 auto;
  display: flex;
  gap: 0;
  background: #fff;
  border: 1px solid var(--pos-border);
  border-radius: 10px;
  padding: 0.25rem;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  overflow-x: auto;
  scrollbar-width: thin;
}

.pos__tab {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0.32rem 0.75rem;
  background: transparent;
  border: 0;
  border-radius: 7px;
  color: var(--pos-muted);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  position: relative;
  transition: color .12s, background .12s;
  white-space: nowrap;
}

.pos__tab:hover {
  color: var(--pos-text);
  background: #f5f7fb;
}

.pos__tab.is-active {
  color: var(--pos-brand);
  background: transparent;
}

/* Bottom-border indicator like the reference's underline */
.pos__tab.is-active::after {
  content: '';
  position: absolute;
  left: 14%;
  right: 14%;
  bottom: -0.25rem;
  height: 2px;
  background: var(--pos-brand-gradient);
  border-radius: 2px;
}

.pos__tab-icon {
  width: 22px;
  height: 22px;
  border-radius: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f1f4f9;
  color: var(--pos-muted);
  flex-shrink: 0;
}

.pos__tab-icon img {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.pos__tab-icon svg {
  width: 14px;
  height: 14px;
}

.pos__tab-icon-fallback {
  font-size: 0.68rem;
  font-weight: 800;
  color: var(--pos-text);
}

.pos__tab-icon--grid {
  background: #f1f4f9;
  color: var(--pos-text);
}

.pos__tab.is-active .pos__tab-icon {
  background: #dff5ea;
  color: var(--pos-brand);
}

.pos__tab.is-active.is-drugs .pos__tab-icon {
  background: #dfe9fd;
  color: #1e3a8a;
}

.pos__tab.is-active.is-drugs::after {
  background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%);
}

.pos__tab.is-active.is-drugs {
  color: #1e3a8a;
}

/* ── Catalogue ── */
.pos__catalogue {
  flex: 1 1 auto;
  background: #fff;
  border: 1px solid var(--pos-border);
  border-radius: 14px;
  padding: 0.9rem;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  min-width: 0;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}

.pos__empty {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  color: var(--pos-muted);
  font-size: 0.9rem;
}

.pos__empty-icon {
  font-size: 2rem;
  opacity: 0.5;
}

.pos__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.85rem;
}

.pos__tile {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid var(--pos-border);
  border-radius: 12px;
  overflow: hidden;
  min-width: 0;
  transition: transform .12s ease, box-shadow .12s ease, border-color .12s ease;
}

.pos__tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 22px rgba(14, 91, 70, 0.10);
  border-color: var(--pos-brand-2);
}

.pos__tile.is-drugs:hover {
  border-color: #2563eb;
  box-shadow: 0 12px 22px rgba(30, 58, 138, 0.10);
}

/* Tile media (image stand-in): soft org-themed bg with shortcode
   rendered large + faded, org logo as a watermark in the corner */
.pos__tile-media {
  position: relative;
  aspect-ratio: 4 / 3;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  cursor: pointer;
  overflow: hidden;
}

.pos__tile.is-agro .pos__tile-media {
  background: linear-gradient(140deg, #f0fbf4 0%, #d8f2e3 100%);
}

.pos__tile.is-drugs .pos__tile-media {
  background: linear-gradient(140deg, #eef4ff 0%, #d9e6fd 100%);
}

.pos__tile.is-default .pos__tile-media {
  background: linear-gradient(140deg, #f5f7fb 0%, #e5e9f2 100%);
}

.pos__tile-watermark {
  position: absolute;
  inset: 0;
  margin: auto;
  width: 65%;
  height: 65%;
  object-fit: contain;
  opacity: 0.18;
  filter: saturate(1.2);
}

.pos__tile-photo {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}

.pos__tile-shortcode {
  position: relative;
  z-index: 1;
  font-size: 1.4rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: rgba(31, 42, 68, 0.55);
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  text-transform: uppercase;
}

.pos__tile.is-agro .pos__tile-shortcode {
  color: rgba(14, 91, 70, 0.75);
}

.pos__tile.is-drugs .pos__tile-shortcode {
  color: rgba(30, 58, 138, 0.75);
}

.pos__tile-orgbadge {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.9);
  color: var(--pos-muted);
  z-index: 2;
}

.pos__tile.is-agro .pos__tile-orgbadge { color: var(--pos-brand); }
.pos__tile.is-drugs .pos__tile-orgbadge { color: #1e3a8a; }

.pos__tile-body {
  padding: 0.6rem 0.75rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
}

.pos__tile-name {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--pos-text);
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.pos__tile-desc {
  margin: 0;
  font-size: 0.74rem;
  color: var(--pos-muted);
  line-height: 1.45;
  min-height: 2.1em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.pos__tile-priceline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  min-width: 0;
  margin-top: 2px;
}

.pos__tile-price {
  font-size: 1rem;
  font-weight: 800;
  color: var(--pos-brand);
  letter-spacing: -0.01em;
}

.pos__tile.is-drugs .pos__tile-price { color: #1e3a8a; }

.pos__tile-sku {
  font-size: 0.7rem;
  color: var(--pos-soft);
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  background: #f1f4f9;
  padding: 2px 7px;
  border-radius: 999px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.pos__tile-add {
  margin-top: 8px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: #fff;
  border: 1.4px solid var(--pos-border);
  border-radius: 8px;
  color: var(--pos-text);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all .12s ease;
}

.pos__tile-add span {
  font-weight: 800;
  color: var(--pos-brand-2);
}

.pos__tile-add:hover {
  background: var(--pos-brand);
  border-color: var(--pos-brand);
  color: #fff;
}

.pos__tile-add:hover span {
  color: #fff;
}

.pos__tile.is-drugs .pos__tile-add:hover {
  background: #1e3a8a;
  border-color: #1e3a8a;
}

/* ─────────────────────────────────────────────────────────────
   Cart panel — spans the full viewport height, anchored to the
   right edge of the screen with a hard divider on the left.
   ───────────────────────────────────────────────────────────── */
.pos__cart {
  height: 100vh;
  background: #fff;
  border-left: 1px solid var(--pos-border);
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  box-shadow: -6px 0 18px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

/* Customer slot at the top of the cart panel — phone search +
   (when found) name + last-contact pill + History chip. Detail
   editing lives in the checkout modal. */
.pos__customer {
  flex: 0 0 auto;
  padding: 0.85rem 0.95rem 0.7rem;
  border-bottom: 1px solid var(--pos-border-soft);
}

.pos__customer-row {
  display: grid;
  grid-template-columns: 120px 1fr auto;
  gap: 6px;
  align-items: center;
}

/* Country selector sits flush with the phone input — same 36px
   height as the input/button so the row aligns on a single baseline. */
.pos__customer-country {
  width: 100%;
  min-width: 0;
}

.pos__customer-country >>> .app-dropdown__trigger {
  height: 36px;
  border-radius: 8px;
  border: 1px solid var(--pos-border);
  background: #fff;
  font-size: 0.82rem;
  padding: 0 0.55rem;
}

.pos__customer-country >>> .app-dropdown__trigger:focus,
.pos__customer-country >>> .app-dropdown__trigger:focus-visible {
  outline: 0;
  border-color: var(--pos-brand-2);
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.16);
}

.pos__customer-phone-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.pos__customer-phone-icon {
  position: absolute;
  left: 10px;
  color: var(--pos-soft);
  pointer-events: none;
}

.pos__customer-phone {
  width: 100%;
  height: 36px;
  border: 1px solid var(--pos-border);
  border-radius: 8px;
  padding: 0 0.65rem 0 30px;
  font-size: 0.88rem;
  color: var(--pos-text);
  background: #fff;
}

.pos__customer-phone:focus {
  outline: 0;
  border-color: var(--pos-brand-2);
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.16);
}

.pos__customer-find {
  height: 36px;
  padding: 0 0.85rem;
  border: 0;
  border-radius: 8px;
  background: var(--pos-brand-gradient);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
}

.pos__customer-find:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pos__hint {
  display: block;
  margin-top: 6px;
  font-size: 0.74rem;
  font-weight: 600;
}

.pos__hint.is-ok { color: #047857; }
.pos__hint.is-warn { color: #b45309; }
.pos__hint.is-muted { color: var(--pos-muted); }

.pos__customer-card {
  margin-top: 0.55rem;
  padding-top: 0.55rem;
  border-top: 1px dashed var(--pos-border-soft);
}

.pos__customer-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--pos-text);
}

.pos__lastcall {
  margin-top: 4px;
  font-size: 0.78rem;
  color: #4b5563;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.pos__lastcall strong { color: var(--pos-text); }

.pos__lastcall-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.22);
}

.pos__lastcall-meta {
  color: var(--pos-muted);
  font-size: 0.74rem;
}

.pos__customer-actions {
  margin-top: 8px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.pos__chip-sm {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 9px;
  background: #fff;
  border: 1px solid var(--pos-border);
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 600;
  color: var(--pos-text);
  cursor: pointer;
}

.pos__chip-sm:hover {
  border-color: var(--pos-brand-2);
  color: var(--pos-brand);
}

.pos__chip-sm--accent {
  border-color: #fde68a;
  background: #fffbeb;
  color: #92400e;
}

.pos__chip-sm--accent:hover {
  background: #fef3c7;
  color: #92400e;
}

.pos__chip-sm-badge {
  background: rgba(0, 0, 0, 0.08);
  border-radius: 999px;
  padding: 1px 6px;
  font-size: 0.68rem;
  font-weight: 800;
}

.pos__customer-edit {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pos__input {
  width: 100%;
  height: 36px;
  padding: 0 0.65rem;
  border: 1px solid var(--pos-border);
  border-radius: 8px;
  font-size: 0.86rem;
  color: var(--pos-text);
  background: #fff;
  font-family: inherit;
}

.pos__input:focus {
  outline: 0;
  border-color: var(--pos-brand-2);
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.16);
}

.pos__input--sm { font-size: 0.86rem; }

.pos__textarea {
  height: auto;
  padding: 0.4rem 0.65rem;
  resize: vertical;
}

/* Mandatory-field marker — small red asterisk pinned to the
   top-right of the field. Used on inputs/textareas that gate the
   Charge button. */
.pos-required-field {
  position: relative;
}

.pos-required-field .pos__textarea {
  padding-right: 1.5rem;
}

.pos-required-mark {
  position: absolute;
  top: 6px;
  right: 8px;
  color: #ef4444;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1;
  pointer-events: none;
}

.pos__pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.pos__triple {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 6px;
}

/* Cart header */
.pos__cart-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.7rem 0.95rem 0.5rem;
}

.pos__cart-head h2 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 800;
  color: var(--pos-text);
  letter-spacing: -0.01em;
}

.pos__cart-count {
  font-size: 0.74rem;
  font-weight: 700;
  color: var(--pos-muted);
  padding: 3px 9px;
  background: #f1f4f9;
  border-radius: 999px;
}

/* Cart lines */
.pos__lines {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 0.6rem 0.5rem;
  min-height: 120px;
}

.pos__lines-empty {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  text-align: center;
  color: var(--pos-muted);
  padding: 1.5rem 0;
}

.pos__lines-empty-big {
  font-size: 0.95rem;
  font-weight: 700;
  color: #4b5563;
}

.pos__lines-empty-sub {
  font-size: 0.78rem;
}

.pos__group {
  margin-bottom: 0.55rem;
}

.pos__group:last-child { margin-bottom: 0; }

.pos__group-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.32rem 0.7rem;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 0.35rem;
}

.pos__group-head.is-agro { background: #dff5ea; color: var(--pos-brand); }
.pos__group-head.is-drugs { background: #dfe9fd; color: #1e3a8a; }
.pos__group-head.is-default { background: #eef1f7; color: #4b5563; }

.pos__line-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Cart line — reference layout: thumb | info | qty | remove */
.pos__line {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) auto 22px;
  gap: 10px;
  align-items: center;
  padding: 0.55rem 0.5rem;
  border-radius: 10px;
  background: #fff;
  min-width: 0;
  transition: background .1s;
}

.pos__line:hover {
  background: #f8fafc;
}

.pos__line-thumb {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.pos__line-thumb.is-agro { background: #dff5ea; }
.pos__line-thumb.is-drugs { background: #dfe9fd; }
.pos__line-thumb.is-default { background: #eef1f7; }

.pos__line-thumb img {
  width: 32px;
  height: 32px;
  object-fit: contain;
  opacity: 0.85;
}

.pos__line-thumb.has-photo {
  background: #f5f7fb;
}

.pos__line-thumb.has-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 1;
}

.pos__line-info {
  min-width: 0;
}

.pos__line-name {
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--pos-text);
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.pos__line-meta {
  font-size: 0.7rem;
  color: var(--pos-soft);
  margin-top: 1px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.pos__line-price {
  font-size: 0.86rem;
  font-weight: 800;
  color: var(--pos-brand);
  margin-top: 2px;
}

.pos__line-qty {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.pos__qty-btn {
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 800;
  font-size: 0.95rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform .08s, opacity .12s;
}

.pos__qty-btn--minus {
  background: #f1f4f9;
  color: var(--pos-muted);
}

.pos__qty-btn--minus:hover:not(:disabled) {
  background: #e2e7ef;
  color: var(--pos-text);
}

.pos__qty-btn--plus {
  background: var(--pos-brand-2);
  color: #fff;
  box-shadow: 0 2px 6px rgba(22, 160, 122, 0.30);
}

.pos__qty-btn--plus:hover {
  background: var(--pos-brand);
}

.pos__qty-btn:active:not(:disabled) {
  transform: scale(0.92);
}

.pos__qty-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.pos__qty-input {
  width: 26px;
  height: 24px;
  border: 0;
  text-align: center;
  font-weight: 800;
  font-size: 0.88rem;
  color: var(--pos-text);
  background: transparent;
  -moz-appearance: textfield;
}

.pos__qty-input::-webkit-outer-spin-button,
.pos__qty-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.pos__line-x {
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--pos-soft);
  font-size: 1.05rem;
  line-height: 1;
  cursor: pointer;
  transition: background .12s, color .12s;
}

.pos__line-x:hover {
  background: #fee2e2;
  color: #b91c1c;
}

/* ─────────────────────────────────────────────────────────────
   Cart footer — running total + Proceed to Checkout button.
   Sits at the bottom of the slim cart panel; the full payment
   detail moves to the checkout modal.
   ───────────────────────────────────────────────────────────── */
.pos__cart-foot {
  flex: 0 0 auto;
  padding: 0.6rem 0.9rem 0.85rem;
  border-top: 1px solid var(--pos-border);
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.pos__running {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pos__running-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-size: 0.82rem;
  color: var(--pos-muted);
}

.pos__running-row--grand {
  font-size: 1.05rem;
  font-weight: 800;
  color: var(--pos-brand);
  letter-spacing: -0.01em;
  padding-top: 4px;
  border-top: 1px dashed var(--pos-border-soft);
}

.pos__running-row--grand > span:first-child {
  color: var(--pos-text);
}

.pos__proceed {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 50px;
  border: 0;
  border-radius: 10px;
  background: var(--pos-brand-gradient);
  color: #fff;
  font-size: 0.95rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(14, 91, 70, 0.28);
  transition: transform .1s ease, box-shadow .1s ease;
}

.pos__proceed:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(14, 91, 70, 0.32);
}

.pos__proceed:active:not(:disabled) {
  transform: translateY(0);
}

.pos__proceed:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  box-shadow: none;
}

.pos__reset-link {
  background: transparent;
  border: 0;
  font-size: 0.74rem;
  font-weight: 600;
  color: var(--pos-muted);
  cursor: pointer;
  text-decoration: underline;
  align-self: center;
  padding: 2px 6px;
}

.pos__reset-link:hover:not(:disabled) {
  color: #b91c1c;
}

.pos__reset-link:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ─────────────────────────────────────────────────────────────
   Detail Payment box (the gray card from the reference)
   ───────────────────────────────────────────────────────────── */
.pos__payment-box {
  margin: 0 0.7rem;
  padding: 0.7rem 0.85rem 0.55rem;
  background: #f5f7fb;
  border-radius: 12px;
  border: 1px solid var(--pos-border-soft);
}

.pos__payment-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e9f2;
  margin-bottom: 0.5rem;
}

.pos__payment-head h3 {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 800;
  color: var(--pos-text);
}

.pos__payment-method {
  height: 28px;
  border: 1px solid var(--pos-border);
  border-radius: 6px;
  padding: 0 0.5rem;
  background: #fff;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--pos-text);
  cursor: pointer;
}

.pos__payment-method:focus {
  outline: 0;
  border-color: var(--pos-brand-2);
  box-shadow: 0 0 0 2px rgba(22, 160, 122, 0.18);
}

.pos__payment-lines {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pos__payment-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.84rem;
  color: var(--pos-muted);
  padding: 2px 0;
}

.pos__payment-amt {
  font-weight: 700;
  color: var(--pos-brand);
}

.pos__payment-amt--neg {
  color: #b91c1c;
}

.pos__payment-row--input {
  display: grid;
  grid-template-columns: 1fr 78px auto;
  gap: 6px;
}

.pos__payment-input-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  color: var(--pos-muted);
}

.pos__payment-mini-mode {
  border: 0;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--pos-muted);
  cursor: pointer;
}

.pos__payment-input {
  height: 24px;
  border: 1px solid var(--pos-border);
  border-radius: 5px;
  padding: 0 0.35rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--pos-text);
  background: #fff;
  text-align: right;
  -moz-appearance: textfield;
}

.pos__payment-input::-webkit-outer-spin-button,
.pos__payment-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.pos__payment-input:focus {
  outline: 0;
  border-color: var(--pos-brand-2);
}

.pos__payment-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.76rem;
  color: var(--pos-muted);
  cursor: pointer;
  margin: 4px 0 0;
}

.pos__payment-toggle input { margin: 0; }

.pos__payment-split {
  margin-top: 6px;
  padding: 4px 8px;
  font-size: 0.72rem;
  text-align: center;
  background: #fef3c7;
  color: #92400e;
  border-radius: 6px;
  font-weight: 600;
}

.pos__payment-total {
  margin: 0.55rem -0.1rem 0;
  padding: 0.55rem 0.1rem 0.1rem;
  border-top: 1px solid var(--pos-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pos__payment-total > span:first-child {
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--pos-text);
}

.pos__payment-total > span:last-child {
  font-size: 1.15rem;
  font-weight: 800;
  color: var(--pos-brand);
  letter-spacing: -0.01em;
}

/* Cash row */
.pos__cash {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 8px;
  align-items: center;
  margin-top: 8px;
  padding: 0.4rem 0.55rem;
  background: #fff;
  border-radius: 8px;
  border: 1px solid var(--pos-border-soft);
}

.pos__cash label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--pos-muted);
  margin: 0;
}

.pos__cash-input {
  height: 28px;
  border: 1px solid var(--pos-border);
  border-radius: 6px;
  padding: 0 0.4rem;
  font-size: 0.84rem;
  font-weight: 700;
  color: var(--pos-text);
  background: #fff;
  text-align: right;
}

.pos__cash-input:focus {
  outline: 0;
  border-color: var(--pos-brand-2);
}

.pos__cash-change {
  font-size: 0.9rem;
  font-weight: 800;
  color: var(--pos-brand);
}

.pos__cash-change.is-short { color: #b91c1c; }

/* ─────────────────────────────────────────────────────────────
   CTA — big green "Place an order" button + secondary row
   ───────────────────────────────────────────────────────────── */
.pos__cta {
  flex: 0 0 auto;
  padding: 0.7rem 0.95rem 0.95rem;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pos__place {
  width: 100%;
  height: 52px;
  border: 0;
  border-radius: 10px;
  background: var(--pos-brand-gradient);
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(14, 91, 70, 0.28);
  transition: transform .1s ease, box-shadow .1s ease;
}

.pos__place:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(14, 91, 70, 0.32);
}

.pos__place:active:not(:disabled) {
  transform: translateY(0);
}

.pos__place:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  box-shadow: none;
}

.pos__cta-sub {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.pos__cta-sub-btn {
  height: 36px;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: #fff;
  border: 1px solid var(--pos-border);
  color: var(--pos-muted);
  transition: all .12s ease;
}

.pos__cta-sub-btn:hover:not(:disabled) {
  border-color: #fca5a5;
  color: #b91c1c;
}

.pos__cta-sub-btn--whatsapp {
  background: #25d366;
  color: #fff;
  border: 0;
}

.pos__cta-sub-btn--whatsapp:hover:not(:disabled) {
  background: #1eba56;
  color: #fff;
}

.pos__cta-sub-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ─────────────────────────────────────────────────────────────
   History modal
   ───────────────────────────────────────────────────────────── */
.pos-modal {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.pos-modal__panel {
  background: #fff;
  width: min(540px, 100%);
  max-height: 86vh;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.4);
}

.pos-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem 1.25rem 0.85rem;
  border-bottom: 1px solid var(--pos-border);
  background: #fbfdfc;
}

.pos-modal__head h2 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 800;
  color: var(--pos-text);
}

.pos-modal__head p {
  margin: 2px 0 0;
  font-size: 0.78rem;
  color: var(--pos-muted);
}

.pos-modal__close {
  background: transparent;
  border: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 1.4rem;
  line-height: 1;
  color: var(--pos-muted);
  cursor: pointer;
}

.pos-modal__close:hover {
  background: #f1f4f9;
  color: var(--pos-text);
}

.pos-modal__body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 1rem 1.25rem 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.pos-modal__block h3 {
  margin: 0 0 0.55rem;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--pos-muted);
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.pos-modal__count {
  background: #eef1f7;
  color: var(--pos-text);
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 800;
}

.pos-modal__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.pos-modal__item {
  background: #fbfcfe;
  border: 1px solid var(--pos-border);
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
}

.pos-modal__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.pos-modal__date {
  margin-top: 2px;
  font-size: 0.74rem;
  color: var(--pos-muted);
}

.pos-modal__quote {
  margin-top: 6px;
  padding: 6px 8px;
  background: #fff7ed;
  border-left: 3px solid #f59e0b;
  border-radius: 4px;
  font-size: 0.82rem;
  color: #92400e;
  font-style: italic;
  line-height: 1.4;
}

.pos-modal__amt {
  font-weight: 800;
  color: var(--pos-brand);
}

.pos-modal__items {
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.78rem;
  color: var(--pos-muted);
}

.pos-modal__items li {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.pos-modal__items li span {
  font-weight: 700;
  color: var(--pos-text);
}

.pos-pill {
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  padding: 2px 8px;
  border-radius: 999px;
  text-transform: uppercase;
}

.pos-pill.is-success { background: #dff5ea; color: #0e5b46; }
.pos-pill.is-warn    { background: #fef3c7; color: #92400e; }
.pos-pill.is-muted   { background: #f3f4f6; color: #6b7280; }
.pos-pill.is-info    { background: #dfe9fd; color: #1e3a8a; }
.pos-pill.is-default { background: #eef1f7; color: #4b5563; }

.pos-modal-enter-active,
.pos-modal-leave-active {
  transition: opacity 0.18s ease;
}

.pos-modal-enter,
.pos-modal-leave-to {
  opacity: 0;
}

/* ─────────────────────────────────────────────────────────────
   Checkout modal — customer + order recap + payment + totals
   all live here, keeping the right cart panel slim.
   ───────────────────────────────────────────────────────────── */
.pos-checkout__panel {
  width: min(640px, 100%);
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}

.pos-checkout__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem 1.25rem 0.85rem;
  border-bottom: 1px solid var(--pos-border);
  background: var(--pos-brand-gradient);
  color: #fff;
}

.pos-checkout__head h2 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 800;
  letter-spacing: -0.01em;
}

.pos-checkout__head p {
  margin: 2px 0 0;
  font-size: 0.82rem;
  opacity: 0.85;
}

.pos-checkout__head .pos-modal__close {
  color: rgba(255, 255, 255, 0.85);
}

.pos-checkout__head .pos-modal__close:hover {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
}

.pos-checkout__body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 1rem 1.25rem 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.pos-checkout__section {
  background: #fbfcfe;
  border: 1px solid var(--pos-border-soft);
  border-radius: 12px;
  padding: 0.8rem 0.9rem 0.9rem;
}

.pos-checkout__sec-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 0.55rem;
}

.pos-checkout__sec-head h3 {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--pos-muted);
}

.pos-checkout__sec-meta {
  font-size: 0.74rem;
  font-weight: 700;
  color: var(--pos-muted);
  padding: 2px 8px;
  border-radius: 999px;
  background: #eef1f7;
}

.pos-checkout__phone-row {
  display: grid;
  grid-template-columns: 120px 1fr auto;
  gap: 6px;
  align-items: center;
}

.pos-checkout__lastcall {
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  font-size: 0.8rem;
  color: #92400e;
}

.pos-checkout__history-chip {
  margin-left: auto;
}

.pos-checkout__fields {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Order items recap inside the modal */
.pos-checkout__items {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 240px;
  overflow-y: auto;
}

.pos-checkout__item {
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 6px 8px;
  background: #fff;
  border: 1px solid var(--pos-border-soft);
  border-radius: 8px;
}

.pos-checkout__item-thumb {
  width: 38px;
  height: 38px;
}

.pos-checkout__item-thumb img {
  width: 26px;
  height: 26px;
}

.pos-checkout__item-info {
  min-width: 0;
}

.pos-checkout__item-name {
  font-size: 0.84rem;
  font-weight: 700;
  color: var(--pos-text);
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pos-checkout__item-meta {
  margin-top: 1px;
  font-size: 0.72rem;
  color: var(--pos-muted);
}

.pos-checkout__item-total {
  font-size: 0.88rem;
  font-weight: 800;
  color: var(--pos-brand);
  white-space: nowrap;
}

/* Payment tiles */
.pos-checkout__pay-tiles {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  margin-bottom: 0.7rem;
}

.pos-checkout__pay-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 72px;
  background: #fff;
  border: 1.5px solid var(--pos-border);
  border-radius: 8px;
  font-size: 0.84rem;
  font-weight: 700;
  color: #4b5563;
  cursor: pointer;
  transition: all .1s ease;
}

.pos-checkout__pay-icon {
  font-size: 1.4rem;
  line-height: 1;
}

.pos-checkout__pay-icon-img {
  width: 26px;
  height: 26px;
  object-fit: contain;
}

.pos-checkout__pay-label {
  line-height: 1.1;
  text-align: center;
}

.pos-checkout__pay-btn:hover {
  border-color: var(--pos-brand-2);
  color: var(--pos-brand);
}

.pos-checkout__pay-btn.is-active {
  background: rgba(22, 160, 122, 0.08);
  color: var(--pos-brand);
  border-color: var(--pos-brand-2);
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.pos-checkout__adjusts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 8px;
}

.pos-checkout__adjust label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--pos-muted);
  margin: 0 0 4px;
}

.pos-checkout__adjust-control {
  display: grid;
  grid-template-columns: 50px 1fr;
  gap: 4px;
}

.pos-checkout__gst-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  color: var(--pos-muted);
  cursor: pointer;
  margin-top: 4px;
}

.pos-checkout__gst-toggle input { margin: 0; }

.pos-checkout__gstin {
  margin-top: 6px;
}

/* Totals section */
.pos-checkout__totals-section {
  background: #f5f7fb;
}

.pos-checkout__totals {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pos-checkout__totals-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.86rem;
  color: var(--pos-muted);
  padding: 2px 0;
}

.pos-checkout__totals-row > span:last-child {
  font-weight: 700;
  color: var(--pos-brand);
}

.pos-checkout__totals-row--neg > span:last-child {
  color: #b91c1c;
}

.pos-checkout__totals-row--note {
  margin-top: 4px;
  padding: 2px 0 0;
  font-size: 0.72rem;
  font-style: italic;
  color: var(--pos-muted);
}

.pos-checkout__totals-row--note > span:last-child {
  font-weight: 600;
  font-style: normal;
  color: var(--pos-muted);
}

.pos-checkout__totals-split {
  margin-top: 6px;
  padding: 4px 8px;
  font-size: 0.72rem;
  text-align: center;
  background: #fef3c7;
  color: #92400e;
  border-radius: 6px;
  font-weight: 600;
}

.pos-checkout__grand {
  margin-top: 0.6rem;
  padding: 0.7rem 0.85rem;
  border-radius: 10px;
  background: var(--pos-brand-gradient);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 4px 14px rgba(14, 91, 70, 0.22);
}

.pos-checkout__grand > span:first-child {
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.85;
}

.pos-checkout__grand > span:last-child {
  font-size: 1.45rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.pos-checkout__cash {
  margin-top: 0.6rem;
  padding: 0.55rem 0.7rem;
  background: #fff;
  border: 1px solid var(--pos-border-soft);
  border-radius: 10px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 10px;
  align-items: center;
}

.pos-checkout__cash label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--pos-muted);
  margin: 0;
}

.pos-checkout__cash-input {
  text-align: right;
}

.pos-checkout__cash-change {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  color: var(--pos-brand);
}

.pos-checkout__cash-change span {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--pos-muted);
}

.pos-checkout__cash-change strong {
  font-size: 0.95rem;
  font-weight: 800;
}

.pos-checkout__cash-change.is-short,
.pos-checkout__cash-change.is-short strong {
  color: #b91c1c;
}

/* Footer with action buttons */
.pos-checkout__foot {
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: auto auto 1fr;
  gap: 8px;
  padding: 0.7rem 1.25rem 1rem;
  border-top: 1px solid var(--pos-border-soft);
  background: #fff;
}

.pos-checkout__cancel {
  height: 44px;
  padding: 0 1rem;
  background: #fff;
  border: 1px solid var(--pos-border);
  border-radius: 9px;
  color: var(--pos-muted);
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: border-color .12s, color .12s;
}

.pos-checkout__cancel:hover:not(:disabled) {
  border-color: var(--pos-text);
  color: var(--pos-text);
}

.pos-checkout__whatsapp {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 44px;
  padding: 0 0.9rem;
  background: #25d366;
  color: #fff;
  border: 0;
  border-radius: 9px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: background .12s;
}

.pos-checkout__whatsapp:hover:not(:disabled) {
  background: #1eba56;
}

.pos-checkout__whatsapp:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pos-checkout__charge {
  height: 44px;
  border: 0;
  border-radius: 9px;
  background: var(--pos-brand-gradient);
  color: #fff;
  font-size: 0.95rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(14, 91, 70, 0.28);
  transition: transform .1s ease, box-shadow .1s ease;
}

.pos-checkout__charge:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(14, 91, 70, 0.34);
}

.pos-checkout__charge:active:not(:disabled) {
  transform: translateY(0);
}

.pos-checkout__charge:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  box-shadow: none;
}

/* ─────────────────────────────────────────────────────────────
   Responsive — tablet → phone
   The cart panel anchors to the right above 960px; below that
   it stacks under the catalogue. On phones we shrink tiles,
   collapse the checkout grids to single column, hide the
   refresh-button text, and let the checkout / history modals
   take the full viewport.
   ───────────────────────────────────────────────────────────── */
@media (max-width: 1200px) {
  .pos { --pos-cart-width: 340px; }
}

@media (max-width: 1024px) {
  .pos { --pos-cart-width: 300px; }
  .pos__grid {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }
}

@media (max-width: 960px) {
  .pos {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto;
    height: auto;
    min-height: 100vh;
  }
  .pos__left {
    padding: 0.6rem 0.7rem;
  }
  .pos__cart {
    height: auto;
    border-left: 0;
    border-top: 1px solid var(--pos-border);
    box-shadow: 0 -6px 18px rgba(15, 23, 42, 0.06);
  }
  .pos__catalogue {
    max-height: 65vh;
  }
}

@media (max-width: 768px) {
  .pos__grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 0.7rem;
  }
  .pos__catalogue { padding: 0.7rem; }
  .pos__tile-body { padding: 0.55rem 0.65rem 0.65rem; }
  .pos__tile-name { font-size: 0.86rem; }
  .pos__tile-desc { font-size: 0.7rem; min-height: 1.8em; }
  .pos__tile-price { font-size: 0.92rem; }
  .pos__tile-add { height: 32px; font-size: 0.78rem; }

  .pos-checkout__adjusts { grid-template-columns: 1fr; }
  .pos-checkout__head { padding: 0.85rem 1rem 0.7rem; }
  .pos-checkout__body { padding: 0.85rem 1rem 0.4rem; gap: 0.75rem; }
  .pos-checkout__section { padding: 0.7rem 0.75rem 0.75rem; }
  .pos-checkout__foot { padding: 0.6rem 1rem 0.8rem; }
}

@media (max-width: 560px) {
  .pos__left {
    padding: 0.55rem 0.55rem;
    gap: 0.55rem;
  }
  .pos__topbar {
    gap: 0.4rem;
  }
  .pos__search-wrap {
    height: 38px;
    padding: 0 10px 0 36px;
  }
  .pos__search-icon { left: 11px; }
  .pos__search { font-size: 0.85rem; }
  .pos__refresh {
    width: 38px;
    height: 38px;
    padding: 0;
    justify-content: center;
  }
  .pos__refresh span { display: none; }
  .pos__ledger { width: 38px; height: 38px; }

  .pos__tabs { padding: 0.2rem; }
  .pos__tab {
    padding: 0.3rem 0.55rem;
    font-size: 0.76rem;
    gap: 6px;
  }
  .pos__tab-icon { width: 20px; height: 20px; }
  .pos__tab-icon svg { width: 12px; height: 12px; }

  .pos__catalogue {
    padding: 0.55rem;
    border-radius: 12px;
  }
  .pos__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
  }
  .pos__tile { border-radius: 10px; }
  .pos__tile-media { aspect-ratio: 5 / 3; }
  .pos__tile-shortcode { font-size: 1rem; }
  .pos__tile-orgbadge { font-size: 0.55rem; padding: 2px 6px; top: 6px; right: 6px; }
  .pos__tile-body { padding: 0.5rem 0.55rem 0.6rem; gap: 3px; }
  .pos__tile-name { font-size: 0.82rem; }
  .pos__tile-desc { font-size: 0.66rem; min-height: 1.6em; }
  .pos__tile-priceline { margin-top: 1px; }
  .pos__tile-price { font-size: 0.88rem; }
  .pos__tile-sku { font-size: 0.62rem; padding: 1px 5px; }
  .pos__tile-add { height: 30px; font-size: 0.74rem; margin-top: 5px; }

  /* Cart anchored at the right collapses under the catalogue */
  .pos__customer { padding: 0.65rem 0.7rem 0.55rem; }
  .pos__cart-head { padding: 0.55rem 0.7rem 0.4rem; }
  .pos__cart-head h2 { font-size: 1rem; }
  .pos__lines { padding: 0 0.45rem 0.4rem; }
  .pos__cart-foot {
    padding: 0.55rem 0.7rem 0.75rem;
    position: sticky;
    bottom: 0;
  }
  .pos__proceed { height: 46px; font-size: 0.9rem; }

  /* Two-row cart line: avatar | (name/meta/price) | remove on top,
     qty controls drop to a second row aligned right. */
  .pos__line {
    grid-template-columns: 38px minmax(0, 1fr) 22px;
    grid-template-rows: auto auto;
    gap: 4px 10px;
    padding: 0.5rem 0.4rem;
  }
  .pos__line-thumb {
    width: 38px;
    height: 38px;
    grid-row: 1 / 3;
  }
  .pos__line-thumb img { width: 26px; height: 26px; }
  .pos__line-info {
    grid-column: 2 / 3;
    grid-row: 1 / 2;
  }
  .pos__line-qty {
    grid-column: 2 / 3;
    grid-row: 2 / 3;
    justify-self: start;
    margin-top: 2px;
  }
  .pos__line-x {
    grid-column: 3 / 4;
    grid-row: 1 / 3;
    align-self: start;
  }

  /* Pincode / District / State stack: 1st row full, then 50/50 */
  .pos__triple {
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }
  .pos__triple > :nth-child(1) { grid-column: 1 / -1; }

  /* Modals — full-screen on phones, no rounded corners, no inset */
  .pos-modal {
    padding: 0;
    align-items: stretch;
  }
  .pos-modal__panel,
  .pos-checkout__panel {
    width: 100%;
    max-width: none;
    max-height: 100vh;
    height: 100vh;
    border-radius: 0;
  }

  .pos-checkout__head { padding: 0.85rem 0.95rem 0.7rem; }
  .pos-checkout__head h2 { font-size: 1rem; }
  .pos-checkout__head p { font-size: 0.76rem; }
  .pos-checkout__body { padding: 0.75rem 0.85rem 0.4rem; gap: 0.65rem; }
  .pos-checkout__section { padding: 0.65rem 0.7rem 0.75rem; }
  .pos-checkout__items { max-height: 200px; }
  .pos-checkout__item {
    grid-template-columns: 32px minmax(0, 1fr) auto;
    padding: 5px 7px;
    gap: 8px;
  }
  .pos-checkout__item-thumb { width: 32px; height: 32px; }
  .pos-checkout__item-thumb img { width: 22px; height: 22px; }
  .pos-checkout__item-name { font-size: 0.8rem; }
  .pos-checkout__item-meta { font-size: 0.68rem; }
  .pos-checkout__item-total { font-size: 0.82rem; }
  .pos-checkout__pay-btn { height: 66px; font-size: 0.78rem; }
  .pos-checkout__grand { padding: 0.6rem 0.75rem; }
  .pos-checkout__grand > span:first-child { font-size: 0.72rem; }
  .pos-checkout__grand > span:last-child { font-size: 1.2rem; }

  /* Tendered + Change reflow onto two rows so the label gets a full
     row of its own and the input/change-line sit underneath. */
  .pos-checkout__cash {
    grid-template-columns: 1fr 1fr;
    gap: 6px 10px;
    padding: 0.5rem 0.6rem;
  }
  .pos-checkout__cash label { grid-column: 1 / -1; }

  .pos-checkout__foot {
    grid-template-columns: 1fr 1fr;
    padding: 0.6rem 0.85rem 0.8rem;
    gap: 6px;
  }
  .pos-checkout__charge {
    grid-column: 1 / -1;
    order: -1;
    height: 48px;
  }
  .pos-checkout__cancel,
  .pos-checkout__whatsapp {
    height: 42px;
    font-size: 0.8rem;
  }

  .pos-modal__head { padding: 0.85rem 0.95rem 0.7rem; }
  .pos-modal__body { padding: 0.85rem 0.95rem 1rem; }
}

@media (max-width: 380px) {
  .pos__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.45rem;
  }
  .pos__tile-name { font-size: 0.78rem; }
  .pos__tile-desc { font-size: 0.64rem; }
  .pos__tile-add { height: 28px; font-size: 0.7rem; }
  .pos__qty-btn { width: 24px; height: 24px; font-size: 0.85rem; }
  .pos__qty-input { width: 22px; font-size: 0.82rem; }
  .pos__tab-label { font-size: 0.74rem; }
}

/* Discount preset banner + locked-field affordances. Kept stylistically
   parallel to TeleSales/OrderForm's `preset-banner`, but using POS's
   pos- prefix so file-scoped styles don't clash. */
.pos-preset-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
  padding: 0.65rem 0.85rem;
  margin-bottom: 10px;
  border-radius: 10px;
  background: linear-gradient(135deg, #ecfdf5 0%, #f5fbf8 100%);
  border: 1px solid #c7ebda;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.06);
  flex-wrap: wrap;
}

.pos-preset-banner__info {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;
}

.pos-preset-banner__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #16a07a;
  box-shadow: 0 0 0 4px rgba(22, 160, 122, 0.18);
  flex-shrink: 0;
}

.pos-preset-banner__title {
  font-size: 0.82rem;
  font-weight: 700;
  color: #0e5b46;
  letter-spacing: -0.01em;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.pos-preset-banner__info-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid #c7ebda;
  background: #ffffff;
  color: #0e5b46;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.pos-preset-banner__info-btn:hover {
  border-color: #16a07a;
  background: #f5fbf8;
  color: #0e5b46;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.12);
}

.pos-preset-banner__info-btn:focus { outline: none; }
.pos-preset-banner__info-btn::-moz-focus-inner { border: 0; }
.pos-preset-banner__info-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.pos-preset-banner__sub {
  font-size: 0.72rem;
  color: #4b5563;
  margin-top: 0.1rem;
}

.pos-preset-banner__toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  cursor: pointer;
  user-select: none;
}

.pos-preset-banner__toggle input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.pos-preset-banner__track {
  position: relative;
  width: 36px;
  height: 20px;
  background: #d1d5db;
  border-radius: 999px;
  transition: background 0.18s ease;
}

.pos-preset-banner__thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background: #ffffff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
  transition: transform 0.18s ease;
}

.pos-preset-banner__toggle.is-on .pos-preset-banner__track {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.pos-preset-banner__toggle.is-on .pos-preset-banner__thumb {
  transform: translateX(16px);
}

.pos-preset-banner__label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #0e5b46;
}

.pos-preset-pill {
  display: inline-flex;
  align-items: center;
  margin-left: 0.4rem;
  padding: 0 0.45rem;
  height: 16px;
  background: #dff5ea;
  color: #0e5b46;
  border-radius: 999px;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0.02em;
}

.pos-preset-pill--muted {
  background: #f1f5f9;
  color: #475569;
}

.pos-checkout__adjust.is-locked {
  opacity: 0.85;
}

.pos-checkout__adjust.is-locked .pos__input,
.pos-checkout__adjust.is-locked .pos__payment-mini-mode {
  background: #f3f4f6;
  cursor: not-allowed;
}

/* Sale success modal — ported from TeleSales.vue. Same animation +
   layout so the post-save UX is consistent across the two billing
   surfaces. Discounted orders flip the title to "Submitted for
   Approval" and disable the action buttons, matching TeleSales. */
.sale-success {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
}

.sale-success__panel {
  position: relative;
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.32), 0 4px 12px rgba(15, 23, 42, 0.08);
  padding: 1.85rem 1.5rem 1.4rem;
  text-align: center;
  animation: sale-success-pop 0.32s cubic-bezier(0.18, 1.2, 0.4, 1) both;
}

.sale-success__close {
  position: absolute;
  top: 0.6rem;
  right: 0.6rem;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.sale-success__close:hover {
  background: #f3f4f6;
  color: #1f2a44;
}

.sale-success__animation {
  position: relative;
  width: 96px;
  height: 96px;
  margin: 0 auto 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.sale-success__ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(22, 160, 122, 0.22);
  transform: scale(0);
  animation: sale-success-ring 1.4s ease-out 0.05s infinite;
}

.sale-success__ring--delay {
  animation-delay: 0.7s;
}

.sale-success__check {
  position: relative;
  width: 96px;
  height: 96px;
  filter: drop-shadow(0 4px 10px rgba(22, 160, 122, 0.28));
}

.sale-success__check-circle {
  fill: none;
  stroke: #16a07a;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 176;
  stroke-dashoffset: 176;
  transform-origin: center;
  transform: rotate(-90deg);
  animation: sale-success-draw 0.55s ease-out 0.1s forwards;
}

.sale-success__check-tick {
  fill: none;
  stroke: #16a07a;
  stroke-width: 4.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 60;
  stroke-dashoffset: 60;
  animation: sale-success-draw-tick 0.32s ease-out 0.55s forwards;
}

.sale-success__title {
  margin: 0 0 0.25rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0e5b46;
  letter-spacing: -0.01em;
}

.sale-success__subtitle {
  margin: 0 0 1rem;
  font-size: 0.85rem;
  color: #4b5563;
}

.sale-success__notice {
  margin: 0 0 1rem;
  padding: 0.55rem 0.85rem;
  background: #fef3c7;
  border: 1px solid #fde68a;
  color: #92400e;
  border-radius: 8px;
  font-size: 0.78rem;
  line-height: 1.4;
  text-align: left;
}

.sale-success__meta {
  margin: 0 auto 1.25rem;
  padding: 0.6rem 0.85rem;
  background: #f8fafc;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  display: grid;
  gap: 0.35rem;
  text-align: left;
  font-size: 0.82rem;
}

.sale-success__meta-row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
}

.sale-success__meta-row dt {
  margin: 0;
  color: #6b7280;
  font-weight: 500;
}

.sale-success__meta-row dd {
  margin: 0;
  color: #1f2a44;
  font-weight: 600;
}

.sale-success__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
}

.sale-success__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  height: 40px;
  padding: 0 0.9rem;
  border-radius: 9px;
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: 0.01em;
  border: 1px solid transparent;
  cursor: pointer;
  font-family: inherit;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.sale-success__btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.sale-success__btn--full {
  grid-column: 1 / -1;
}

.sale-success__btn--ghost {
  background: #ffffff;
  border-color: #d8dde6;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.sale-success__btn--ghost:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f0fbf6;
}

.sale-success__btn--accounts {
  background: #f0fbf6;
  border-color: #b7e3d1;
  color: #0e5b46;
  box-shadow: 0 1px 2px rgba(14, 91, 70, 0.05);
}

.sale-success__btn--accounts:hover:not(:disabled) {
  border-color: #16a07a;
  background: #e3f6ec;
  color: #0a4c39;
}

.sale-success__btn--accounts:focus { outline: none; }
.sale-success__btn--accounts::-moz-focus-inner { border: 0; }
.sale-success__btn--accounts:focus-visible {
  outline: none;
  box-shadow: 0 1px 2px rgba(14, 91, 70, 0.05), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.sale-success__btn--copied {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 4px 14px rgba(14, 91, 70, 0.32);
  animation: sale-success-copied-pop 0.28s ease-out;
  cursor: default;
}

@keyframes sale-success-copied-pop {
  0%   { transform: scale(0.96); }
  60%  { transform: scale(1.03); }
  100% { transform: scale(1); }
}

.sale-success__btn--primary {
  background: linear-gradient(135deg, #15a25b 0%, #25d366 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.32);
}

.sale-success__btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(37, 211, 102, 0.4);
  filter: brightness(1.05);
}

.sale-success__btn--primary:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.96);
}

.sale-success-fade-enter-active,
.sale-success-fade-leave-active {
  transition: opacity 0.18s ease;
}

.sale-success-fade-enter,
.sale-success-fade-leave-to {
  opacity: 0;
}

@keyframes sale-success-pop {
  0%   { opacity: 0; transform: translateY(8px) scale(0.96); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes sale-success-draw {
  to { stroke-dashoffset: 0; }
}

@keyframes sale-success-draw-tick {
  to { stroke-dashoffset: 0; }
}

@keyframes sale-success-ring {
  0%   { transform: scale(0.4); opacity: 0.55; }
  80%  { opacity: 0; }
  100% { transform: scale(1.35); opacity: 0; }
}

@media (max-width: 480px) {
  .sale-success__actions {
    grid-template-columns: 1fr;
  }
}
</style>
